import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  //   Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Form,
  ListGroup,
  Modal,
  Offcanvas,
  OverlayTrigger,
  Row,
  Spinner,
  Tab,
  Tabs,
  Tooltip,
} from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  buildDppCompletenessChecks,
  calculateDppCompletenessScore,
  type DppCompletenessCheck,
} from '../components/products/dppCompleteness';
import {
  PublicProductDppView,
  type PublicProductDppPayload,
} from '../components/products/PublicProductDppView';
import type { ProductMetadata } from '../components/products/productFormTypes';
import { QRCodeSVG } from 'qrcode.react';
import { useAppSelector } from '../hooks/hooks';
import { useDeleteDocument } from '../hooks/useDeleteDocument';
import { useUploadDocument } from '../hooks/useUploadDocument';
import { captureSupabaseError } from '../lib/sentry';
import { supabase } from '../lib/supabase';
import { tracking } from '../util/tracking';

type ProductCategory = {
  name?: string | null;
  code?: string | null;
};

type ProductSupplier = {
  company_name?: string | null;
};

type ProductDetailsRecord = {
  id: string;
  name: string;
  sku_model: string;
  gtin_ean: string | null;
  brand_name: string;
  description: string | null;
  image_url: string | null;
  metadata?: ProductMetadata | null;
  created_at: string;
  updated_at: string;
  product_categories?: ProductCategory | ProductCategory[] | null;
  suppliers?: ProductSupplier | ProductSupplier[] | null;
};

type PassportRecord = {
  id: string;
  unique_product_identifier: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'WITHDRAWN';
  public_url: string | null;
  qr_code_asset_url: string | null;
  published_at: string | null;
  visibility_settings?: {
    technical_specs_access?: 'restricted' | 'public';
  } | null;
};

type LifecycleAction = 'DISCONTINUE' | 'WITHDRAW';

type DppAccessTier = 'RESTRICTED' | 'CONFIDENTIAL';

type DppAccessRequestRole =
  | 'RECYCLER_REPAIRER'
  | 'AUDITOR_SURVEILLANCE'
  | 'B2B_BUYER'
  | 'OTHER';

type DppAccessRequestStatus = 'PENDING' | 'GRANTED' | 'REJECTED';

type DppAccessRequestRecord = {
  id: string;
  full_name: string;
  email: string;
  organization: string;
  role: DppAccessRequestRole;
  reason: string;
  status: DppAccessRequestStatus;
  granted_tier: DppAccessTier | null;
  requested_at: string;
  reviewed_at: string | null;
};

type DppAccessTokenRecord = {
  id: string;
  email: string;
  full_name: string | null;
  organization: string | null;
  role: DppAccessRequestRole | null;
  access_tier: DppAccessTier;
  expires_at: string | null;
  created_at: string;
  granted_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

type MaterialRecord = {
  id: string;
  material_name: string;
  weight_percentage: number;
  recycled_content_percentage: number | null;
  is_recyclable: boolean;
};

type PackagingRecord = {
  id: string;
  packaging_category: string;
  material_type: string;
  weight_grams: number;
  pcr_content_percent: number;
  recyclability_status: string | null;
  epr_registration_number: string | null;
  created_at: string;
  updated_at: string;
};

type ProductDocument = {
  id: string;
  title: string | null;
  description: string | null;
  uploaded_at: string | null;
  storage_path: string | null;
  metadata?: Record<string, unknown> | null;
};

type DocumentAccessLevel = 'public' | 'restricted';

type DocumentFileSource = 'file' | 'external_url';

type ComplianceDocumentCategory =
  | 'EU Declaration of Conformity'
  | 'Safety Data Sheet (SDS)'
  | 'Lab Test Report'
  | 'Third-Party Certificate'
  | 'Other';

type ProductDocumentMetadata = {
  managed_in?: 'product_details';
  document_category?: ComplianceDocumentCategory;
  access_level?: DocumentAccessLevel;
  expiration_date?: string | null;
  file_source?: DocumentFileSource;
  external_url?: string | null;
};

type DeclarationRecord = {
  id: string;
  declaration_type: string;
  status: string;
  svhc_present: boolean | null;
  substance_name: string | null;
  cas_ec_number: string | null;
  present_in_component: string | null;
  scip_number: string | null;
  document_id?: string | null;
  documents?: ProductDocument | ProductDocument[] | null;
};

type CircularityRecord = {
  carbon_footprint_kg_co2e: number | null;
  expected_lifetime_years: number | null;
  repairability_score: number | null;
  repair_manual_url: string | null;
  disassembly_guide_url: string | null;
  recycling_instructions: string | null;
  rated_capacity_ah: number | null;
  rated_energy_kwh: number | null;
  nominal_voltage_v: number | null;
  minimum_voltage_v: number | null;
  maximum_voltage_v: number | null;
  power_capability_limits: string | null;
  round_trip_efficiency_percent: number | null;
  cycle_lifetime_descriptor: string | null;
  operating_temperature_window: string | null;
  extinguishing_agents: string | null;
  safety_handling_measures: string | null;
  water_consumption_liters: number | null;
  color_fastness_rating: number | null;
  pilling_resistance_rating: number | null;
  spare_parts_availability: string | null;
  spare_parts_availability_years: number | null;
  spare_parts_catalog_url: string | null;
  take_back_scheme: string | null;
  microplastics_status: string | null;
  epr_registration_number: string | null;
  care_symbols: string[] | null;
  software_support_years: number | null;
};

type AuditEvent = {
  id: string;
  timestamp: string;
  title: string;
  detail: string;
};

type AttachedDocument = {
  id: string;
  title: string;
  description: string | null;
  uploaded_at: string | null;
  storage_path: string | null;
  source: string;
  category: string | null;
  accessLevel: DocumentAccessLevel | null;
  expirationDate: string | null;
  fileSource: DocumentFileSource;
  externalUrl: string | null;
  canManage: boolean;
  declarationId: string | null;
  documentId: string | null;
  kind: 'compliance' | 'guide' | 'category-specific';
};

type ComplianceDocumentFormState = {
  category: ComplianceDocumentCategory;
  title: string;
  fileSource: DocumentFileSource;
  accessLevel: DocumentAccessLevel;
  expirationDate: string;
  externalUrl: string;
  file: File | null;
};

type EditableSection =
  | 'basic-info'
  | 'bom-svhc'
  | 'packaging'
  | 'eco-specs'
  | null;

const MANAGED_DOCUMENT_LIMIT = 3;

type BasicInfoFormState = {
  name: string;
  skuModel: string;
  brandName: string;
  gtinEan: string;
  categoryCode: string;
  description: string;
};

type EditableMaterial = {
  material_name: string;
  weight_percentage: string;
  recycled_content_percentage: string;
  is_recyclable: boolean;
};

type EditableSvhcDeclaration = {
  substance_name: string;
  cas_ec_number: string;
  present_in_component: string;
  scip_number: string;
  document_id: string | null;
};

type BomSvhcFormState = {
  materials: EditableMaterial[];
  svhcStatus: 'none' | 'contains' | '';
  svhcDeclarations: EditableSvhcDeclaration[];
};

type EditablePackaging = {
  packaging_category: string;
  material_type: string;
  weight_grams: string;
  pcr_content_percent: string;
  recyclability_status: string;
};

type PackagingFormState = {
  packagingItems: EditablePackaging[];
  packagingEprRegistration: string;
};

type EcoSpecsFormState = {
  carbonFootprint: string;
  expectedLifetimeYears: string;
  repairabilityScore: string;
  repairGuideUrl: string;
  recyclingInstructions: string;
  waterConsumption: string;
  colorFastnessRating: string;
  pillingResistanceRating: string;
  sparePartsAvailability: string;
  takeBackScheme: string;
  microplasticsStatus: 'not_applicable' | 'untested' | 'tested' | '';
  eprRegistrationNumber: string;
  careSymbols: string;
};

const categoryOptions = [
  { code: 'ELECTRONICS_ICT', name: 'Electronics & ICT Equipment' },
  { code: 'BATTERIES_STORAGE', name: 'Batteries & Energy Storage' },
  { code: 'TEXTILES_APPAREL', name: 'Textiles & Apparel' },
  { code: 'INDUSTRIAL_MACHINERY', name: 'Industrial Machinery' },
  { code: 'CONSTRUCTION_PRODUCTS', name: 'Construction Products' },
  { code: 'FURNITURE_MATTRESSES', name: 'Furniture & Mattresses' },
  { code: 'IRON_STEEL_METALS', name: 'Iron, Steel & Base Metals' },
  { code: 'TIRES_AUTOMOTIVE', name: 'Tires & Vehicle Components' },
  { code: 'CHEMICALS', name: 'Chemicals & Materials' },
  { code: 'OTHER_HARDWARE', name: 'Other / General Hardware' },
];

const complianceDocumentCategoryOptions: ComplianceDocumentCategory[] = [
  'EU Declaration of Conformity',
  'Safety Data Sheet (SDS)',
  'Lab Test Report',
  'Third-Party Certificate',
  'Other',
];

const complianceDocumentMaxBytes = 25 * 1024 * 1024;

const initialComplianceDocumentForm: ComplianceDocumentFormState = {
  category: 'EU Declaration of Conformity',
  title: '',
  fileSource: 'file',
  accessLevel: 'restricted',
  expirationDate: '',
  externalUrl: '',
  file: null,
};

function getFirstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

// function formatPercent(value?: number | null) {
//   if (value === null || value === undefined || Number.isNaN(value)) return '-';
//   return `${value}%`;
// }

function formatNumber(value?: number | null, suffix = '') {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${Number(value).toLocaleString()}${suffix}`;
}

function titleCase(value: string) {
  return value
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

// function toEditableString(value?: number | null) {
//   if (value === null || value === undefined || Number.isNaN(value)) return '';
//   return String(value);
// }

function asOptionalNumber(value: string) {
  return value.trim() === '' ? null : Number(value);
}

function getFileNameFromPath(value?: string | null) {
  if (!value) return 'Attached file';
  const normalizedValue = value.split('?')[0] ?? value;
  const segments = normalizedValue.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? normalizedValue;
}

function getDetectedBatteryPfasMaterials(materialRows: MaterialRecord[]) {
  const patterns: Array<{ label: string; regex: RegExp }> = [
    { label: 'PVDF', regex: /\bpvdf\b|polyvinylidene fluoride/i },
    { label: 'PTFE', regex: /\bptfe\b|polytetrafluoroethylene/i },
    { label: 'FEP', regex: /\bfep\b|fluorinated ethylene propylene/i },
    { label: 'ETFE', regex: /\betfe\b/i },
    { label: 'PVF', regex: /\bpvf\b|polyvinyl fluoride/i },
    { label: 'Fluoroelastomer', regex: /fluoroelastomer|\bfkm\b/i },
    { label: 'LiPF6', regex: /\blipf6\b|lithium hexafluorophosphate/i },
    {
      label: 'Fluoroethylene Carbonate',
      regex: /fluoroethylene carbonate/i,
    },
    { label: 'Fluoropolymer', regex: /fluoropolymer/i },
  ];

  const detected = new Set<string>();

  for (const row of materialRows) {
    const materialName = row.material_name ?? '';
    for (const pattern of patterns) {
      if (pattern.regex.test(materialName)) {
        detected.add(pattern.label);
      }
    }
  }

  return Array.from(detected);
}

function parseProductDocumentMetadata(
  value?: Record<string, unknown> | null,
): ProductDocumentMetadata {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const metadata = value as Record<string, unknown>;

  return {
    managed_in:
      metadata.managed_in === 'product_details' ? 'product_details' : undefined,
    document_category: complianceDocumentCategoryOptions.includes(
      metadata.document_category as ComplianceDocumentCategory,
    )
      ? (metadata.document_category as ComplianceDocumentCategory)
      : undefined,
    access_level:
      metadata.access_level === 'public' ||
      metadata.access_level === 'restricted'
        ? (metadata.access_level as DocumentAccessLevel)
        : undefined,
    expiration_date:
      typeof metadata.expiration_date === 'string'
        ? metadata.expiration_date
        : null,
    file_source:
      metadata.file_source === 'file' || metadata.file_source === 'external_url'
        ? (metadata.file_source as DocumentFileSource)
        : undefined,
    external_url:
      typeof metadata.external_url === 'string' ? metadata.external_url : null,
  };
}

const WOOD_OR_LEATHER_PATTERN =
  /\b(wood|timber|oak|pine|beech|birch|plywood|veneer|leather|hide|suede)\b/i;

function containsWoodOrLeatherMaterials(materials: MaterialRecord[]) {
  return materials.some((material) =>
    WOOD_OR_LEATHER_PATTERN.test(material.material_name ?? ''),
  );
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
}

export default function ProductDetails() {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const selectedOrganisation = useAppSelector(
    (state) => state.organisation.selected,
  );
  const { user } = useAppSelector((state) => state.auth);
  const {
    uploadDocument: uploadProductImage,
    uploading: imageUploading,
    error: imageUploadError,
  } = useUploadDocument();
  const {
    uploadDocument: uploadComplianceDocument,
    uploading: documentUploading,
    error: documentUploadError,
  } = useUploadDocument();
  const { deleting: documentDeleting } = useDeleteDocument();
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const complianceDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const qrCodeContainerRef = useRef<HTMLDivElement | null>(null);
  const [product, setProduct] = useState<ProductDetailsRecord | null>(null);
  const [passport, setPassport] = useState<PassportRecord | null>(null);
  const [materials, setMaterials] = useState<MaterialRecord[]>([]);
  const [packaging, setPackaging] = useState<PackagingRecord[]>([]);
  const [declarations, setDeclarations] = useState<DeclarationRecord[]>([]);
  const [circularity, setCircularity] = useState<CircularityRecord | null>(
    null,
  );
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [publishConfirmDataAccuracy, setPublishConfirmDataAccuracy] =
    useState(false);
  const [publishConfirmPublicUrl, setPublishConfirmPublicUrl] = useState(false);
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [lifecycleAction, setLifecycleAction] =
    useState<LifecycleAction>('DISCONTINUE');
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [lifecycleSaving, setLifecycleSaving] = useState(false);
  const [imageActionError, setImageActionError] = useState<string | null>(null);
  const [documentActionError, setDocumentActionError] = useState<string | null>(
    null,
  );
  const [documentActionSuccess, setDocumentActionSuccess] = useState<
    string | null
  >(null);
  const [accessRequests, setAccessRequests] = useState<
    DppAccessRequestRecord[]
  >([]);
  const [accessTokens, setAccessTokens] = useState<DppAccessTokenRecord[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessMessage, setAccessMessage] = useState<string | null>(null);
  const [accessActionLoading, setAccessActionLoading] = useState(false);
  const [directLinkTier, setDirectLinkTier] =
    useState<DppAccessTier>('RESTRICTED');
  const [directLinkExpiry, setDirectLinkExpiry] = useState<
    '24_HOURS' | '7_DAYS' | '30_DAYS' | 'NO_EXPIRY'
  >('7_DAYS');
  const [generatedDirectLink, setGeneratedDirectLink] = useState<string | null>(
    null,
  );
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentSaving, setDocumentSaving] = useState(false);
  const [documentDropActive, setDocumentDropActive] = useState(false);
  const [editingDocument, setEditingDocument] =
    useState<AttachedDocument | null>(null);
  const [documentForm, setDocumentForm] = useState<ComplianceDocumentFormState>(
    initialComplianceDocumentForm,
  );
  const [activeSection, setActiveSection] = useState<EditableSection>(null);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [basicInfoForm, setBasicInfoForm] = useState<BasicInfoFormState>({
    name: '',
    skuModel: '',
    brandName: '',
    gtinEan: '',
    categoryCode: '',
    description: '',
  });
  const [bomSvhcForm, setBomSvhcForm] = useState<BomSvhcFormState>({
    materials: [],
    svhcStatus: '',
    svhcDeclarations: [],
  });
  const [packagingForm, setPackagingForm] = useState<PackagingFormState>({
    packagingItems: [],
    packagingEprRegistration: '',
  });
  const [ecoSpecsForm, setEcoSpecsForm] = useState<EcoSpecsFormState>({
    carbonFootprint: '',
    expectedLifetimeYears: '',
    repairabilityScore: '',
    repairGuideUrl: '',
    recyclingInstructions: '',
    waterConsumption: '',
    colorFastnessRating: '',
    pillingResistanceRating: '',
    sparePartsAvailability: '',
    takeBackScheme: '',
    microplasticsStatus: '',
    eprRegistrationNumber: '',
    careSymbols: '',
  });

  const loadProduct = useCallback(async () => {
    if (!productId) {
      setError('Product ID is missing.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const [
      productResult,
      passportResult,
      materialsResult,
      packagingResult,
      declarationsResult,
      circularityResult,
    ] = await Promise.all([
      supabase
        .from('products')
        .select(
          'id, name, sku_model, gtin_ean, brand_name, description, image_url, metadata, created_at, updated_at, product_categories(name, code), suppliers(company_name)',
        )
        .eq('id', productId)
        .single(),
      supabase
        .from('product_passports')
        .select(
          'id, unique_product_identifier, status, public_url, qr_code_asset_url, published_at, visibility_settings',
        )
        .eq('product_id', productId)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('product_materials')
        .select(
          'id, material_name, weight_percentage, recycled_content_percentage, is_recyclable',
        )
        .eq('product_id', productId)
        .order('weight_percentage', { ascending: false }),
      supabase
        .from('product_packaging')
        .select(
          'id, packaging_category, material_type, weight_grams, pcr_content_percent, recyclability_status, epr_registration_number, created_at, updated_at',
        )
        .eq('product_id', productId)
        .order('created_at', { ascending: false }),
      supabase
        .from('product_declarations')
        .select(
          'id, declaration_type, status, svhc_present, substance_name, cas_ec_number, present_in_component, scip_number, document_id, documents(id, title, description, uploaded_at, storage_path, metadata)',
        )
        .eq('product_id', productId)
        .order('declaration_type', { ascending: true }),
      supabase
        .from('product_circularity')
        .select(
          'carbon_footprint_kg_co2e, expected_lifetime_years, repairability_score, repair_manual_url, disassembly_guide_url, recycling_instructions, rated_capacity_ah, rated_energy_kwh, nominal_voltage_v, minimum_voltage_v, maximum_voltage_v, power_capability_limits, round_trip_efficiency_percent, cycle_lifetime_descriptor, operating_temperature_window, extinguishing_agents, safety_handling_measures, water_consumption_liters, color_fastness_rating, pilling_resistance_rating, spare_parts_availability, spare_parts_availability_years, spare_parts_catalog_url, take_back_scheme, microplastics_status, epr_registration_number, care_symbols, software_support_years',
        )
        .eq('product_id', productId)
        .maybeSingle(),
    ]);

    const failingResult = [
      productResult,
      passportResult,
      materialsResult,
      packagingResult,
      declarationsResult,
      circularityResult,
    ].find((result) => result.error);

    if (failingResult?.error) {
      setError(failingResult.error.message);
      setProduct(null);
      setPassport(null);
      setMaterials([]);
      setPackaging([]);
      setDeclarations([]);
      setCircularity(null);
    } else {
      setProduct(productResult.data as ProductDetailsRecord);
      setPassport(passportResult.data as PassportRecord | null);
      setMaterials((materialsResult.data ?? []) as MaterialRecord[]);
      setPackaging((packagingResult.data ?? []) as PackagingRecord[]);
      setDeclarations((declarationsResult.data ?? []) as DeclarationRecord[]);
      setCircularity(circularityResult.data as CircularityRecord | null);
    }

    setLoading(false);
  }, [productId]);

  useEffect(() => {
    void loadProduct();
  }, [loadProduct]);

  useEffect(() => {
    const loadAccessManagement = async () => {
      if (!productId || !passport?.id) {
        setAccessRequests([]);
        setAccessTokens([]);
        return;
      }

      setAccessLoading(true);
      setAccessError(null);

      const [requestsResult, tokensResult] = await Promise.all([
        supabase
          .from('dpp_access_requests')
          .select(
            'id, full_name, email, organization, role, reason, status, granted_tier, requested_at, reviewed_at',
          )
          .eq('passport_id', passport.id)
          .order('requested_at', { ascending: false }),
        supabase
          .from('dpp_access_tokens')
          .select(
            'id, email, full_name, organization, role, access_tier, expires_at, created_at, granted_at, last_used_at, revoked_at',
          )
          .eq('passport_id', passport.id)
          .order('created_at', { ascending: false }),
      ]);

      if (requestsResult.error || tokensResult.error) {
        setAccessError(
          requestsResult.error?.message ||
            tokensResult.error?.message ||
            'Unable to load access management data.',
        );
        setAccessLoading(false);
        return;
      }

      setAccessRequests(
        (requestsResult.data ?? []) as DppAccessRequestRecord[],
      );
      setAccessTokens((tokensResult.data ?? []) as DppAccessTokenRecord[]);
      setAccessLoading(false);
    };

    void loadAccessManagement();
  }, [passport?.id, productId]);

  const handlePublishPassport = useCallback(async () => {
    if (!productId) {
      setError('Product ID is missing.');
      return;
    }

    setPublishing(true);
    setPublishMessage(null);
    setError(null);

    const { data, error: publishError } = await supabase.functions.invoke(
      'publish-product-passport',
      {
        body: { product_id: productId },
      },
    );

    if (publishError) {
      captureSupabaseError(publishError, {
        op: 'product-passport.publish',
        productId,
      });
      setError(publishError.message);
      setPublishing(false);
      return;
    }

    if (!data) {
      captureSupabaseError(
        new Error('Passport publish did not return a result.'),
        {
          op: 'product-passport.publish',
          productId,
        },
      );
      setError('Passport publish did not return a result.');
      setPublishing(false);
      return;
    }

    setPublishMessage(
      'Passport is now published and available on the public DPP page.',
    );
    void tracking.log('UI', 'product_publish', {
      productId,
      passportId: data.passport?.id ?? null,
      publicUrl: data.passport?.publicUrl ?? null,
      status: data.passport?.status ?? null,
    });
    setShowPublishConfirmModal(false);
    setPublishConfirmDataAccuracy(false);
    setPublishConfirmPublicUrl(false);
    setPublishing(false);
    await loadProduct();
  }, [loadProduct, productId]);

  const openPublishConfirmModal = useCallback(() => {
    setPublishConfirmDataAccuracy(false);
    setPublishConfirmPublicUrl(false);
    setShowPublishConfirmModal(true);
  }, []);

  const closePublishConfirmModal = useCallback(() => {
    if (publishing) return;
    setShowPublishConfirmModal(false);
    setPublishConfirmDataAccuracy(false);
    setPublishConfirmPublicUrl(false);
  }, [publishing]);

  const openLifecycleModal = useCallback(() => {
    setLifecycleAction('DISCONTINUE');
    setLifecycleReason('');
    setLifecycleError(null);
    setShowLifecycleModal(true);
  }, []);

  const closeLifecycleModal = useCallback(() => {
    if (lifecycleSaving) return;
    setShowLifecycleModal(false);
    setLifecycleReason('');
    setLifecycleError(null);
  }, [lifecycleSaving]);

  const handleLifecycleUpdate = useCallback(async () => {
    if (!productId) {
      setLifecycleError('Product ID is missing.');
      return;
    }

    if (!lifecycleReason.trim()) {
      setLifecycleError('A lifecycle reason is required.');
      return;
    }

    setLifecycleSaving(true);
    setLifecycleError(null);
    setPublishMessage(null);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'delete-product',
        {
          body: {
            product_id: productId,
            action: lifecycleAction,
            reason: lifecycleReason.trim(),
          },
        },
      );

      if (invokeError) {
        throw invokeError;
      }

      if (!data?.success) {
        throw new Error('Product lifecycle update failed.');
      }

      setPublishMessage(
        lifecycleAction === 'WITHDRAW'
          ? 'Product passport has been withdrawn.'
          : 'Product passport has been discontinued.',
      );
      setShowLifecycleModal(false);
      setLifecycleReason('');
      await loadProduct();
    } catch (updateError) {
      setLifecycleError(
        updateError instanceof Error
          ? updateError.message
          : 'Unable to update product lifecycle.',
      );
    } finally {
      setLifecycleSaving(false);
    }
  }, [lifecycleAction, lifecycleReason, loadProduct, productId]);

  const reloadAccessManagement = useCallback(async () => {
    if (!productId || !passport?.id) {
      setAccessRequests([]);
      setAccessTokens([]);
      return;
    }

    setAccessLoading(true);

    const [requestsResult, tokensResult] = await Promise.all([
      supabase
        .from('dpp_access_requests')
        .select(
          'id, full_name, email, organization, role, reason, status, granted_tier, requested_at, reviewed_at',
        )
        .eq('passport_id', passport.id)
        .order('requested_at', { ascending: false }),
      supabase
        .from('dpp_access_tokens')
        .select(
          'id, email, full_name, organization, role, access_tier, expires_at, created_at, granted_at, last_used_at, revoked_at',
        )
        .eq('passport_id', passport.id)
        .order('created_at', { ascending: false }),
    ]);

    if (requestsResult.error || tokensResult.error) {
      setAccessError(
        requestsResult.error?.message ||
          tokensResult.error?.message ||
          'Unable to refresh access management data.',
      );
      setAccessLoading(false);
      return;
    }

    setAccessRequests((requestsResult.data ?? []) as DppAccessRequestRecord[]);
    setAccessTokens((tokensResult.data ?? []) as DppAccessTokenRecord[]);
    setAccessLoading(false);
  }, [passport?.id, productId]);

  const handleAccessDecision = useCallback(
    async (
      action: 'grant' | 'reject' | 'generate_link',
      options?: {
        requestId?: string;
        tier?: DppAccessTier;
      },
    ) => {
      if (!productId || !passport?.id) {
        setAccessError('Passport access management is unavailable.');
        return;
      }

      setAccessActionLoading(true);
      setAccessError(null);
      setAccessMessage(null);

      const { data, error: invokeError } = await supabase.functions.invoke(
        'manage-dpp-access',
        {
          body: {
            action,
            requestId: options?.requestId,
            passportId: passport.id,
            productId,
            tier: options?.tier ?? directLinkTier,
            expiry: directLinkExpiry,
            publicUrl: passport.public_url,
          },
        },
      );

      if (invokeError) {
        setAccessError(invokeError.message);
        setAccessActionLoading(false);
        return;
      }

      if (action === 'reject') {
        setAccessMessage('Access request rejected.');
      } else if (data?.magicUrl) {
        setGeneratedDirectLink(data.magicUrl as string);
        setAccessMessage(
          action === 'generate_link'
            ? 'Direct access link generated.'
            : data?.emailSent === false
              ? 'Access granted. Link created, but email delivery is unavailable.'
              : 'Access granted and link issued.',
        );
      }

      await reloadAccessManagement();
      setAccessActionLoading(false);
    },
    [
      directLinkExpiry,
      directLinkTier,
      passport?.id,
      passport?.public_url,
      productId,
      reloadAccessManagement,
    ],
  );

  const copyGeneratedDirectLink = useCallback(async () => {
    if (!generatedDirectLink) return;

    await navigator.clipboard.writeText(generatedDirectLink);
    setAccessMessage('Direct access link copied to clipboard.');
  }, [generatedDirectLink]);

  const copyPublicDppUrl = useCallback(async () => {
    if (!passport?.public_url) return;

    await navigator.clipboard.writeText(passport.public_url);
  }, [passport?.public_url]);

  const qrCodeFileBaseName = useMemo(() => {
    const fileLabel =
      passport?.unique_product_identifier?.trim() || product?.sku_model?.trim();

    return (
      (fileLabel || 'product-dpp-qr-code')
        .replace(/[^a-z0-9-_]+/gi, '-')
        .replace(/^-+|-+$/g, '') || 'product-dpp-qr-code'
    );
  }, [passport?.unique_product_identifier, product?.sku_model]);

  const getQrCodeSvgMarkup = useCallback(() => {
    const svg = qrCodeContainerRef.current?.querySelector('svg');

    if (!svg) return null;

    const serializer = new XMLSerializer();
    return serializer.serializeToString(svg);
  }, []);

  const triggerQrCodeDownload = useCallback(
    (blob: Blob, extension: 'svg' | 'png') => {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = blobUrl;
      link.download = `${qrCodeFileBaseName}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    },
    [qrCodeFileBaseName],
  );

  const downloadQrCodeAsSvg = useCallback(() => {
    const svgString = getQrCodeSvgMarkup();

    if (!svgString) return;

    const blob = new Blob([svgString], {
      type: 'image/svg+xml;charset=utf-8',
    });

    triggerQrCodeDownload(blob, 'svg');
  }, [getQrCodeSvgMarkup, triggerQrCodeDownload]);

  const downloadQrCodeAsPng = useCallback(() => {
    const svgString = getQrCodeSvgMarkup();

    if (!svgString) return;

    const svgBlob = new Blob([svgString], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const blobUrl = URL.createObjectURL(svgBlob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 1024;

      canvas.width = size;
      canvas.height = size;

      const context = canvas.getContext('2d');

      if (!context) {
        URL.revokeObjectURL(blobUrl);
        return;
      }

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, size, size);
      context.drawImage(img, 0, 0, size, size);

      URL.revokeObjectURL(blobUrl);

      canvas.toBlob((pngBlob) => {
        if (!pngBlob) return;

        triggerQrCodeDownload(pngBlob, 'png');
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
    };

    img.src = blobUrl;
  }, [getQrCodeSvgMarkup, triggerQrCodeDownload]);

  const category = getFirstRelation(product?.product_categories);
  const supplier = getFirstRelation(product?.suppliers);
  const productStatus = passport?.status ?? 'DRAFT';
  const isDraft = productStatus === 'DRAFT';
  const isPublished = productStatus === 'PUBLISHED';
  const isArchived = productStatus === 'ARCHIVED';
  const isWithdrawn = productStatus === 'WITHDRAWN';
  const publishedDppUrl = isPublished ? (passport?.public_url ?? null) : null;
  const productStatusBadge =
    productStatus === 'PUBLISHED'
      ? 'success'
      : productStatus === 'ARCHIVED'
        ? 'warning'
        : productStatus === 'WITHDRAWN'
          ? 'danger'
          : 'secondary';
  const pendingAccessRequestsCount = accessRequests.filter(
    (item) => item.status === 'PENDING',
  ).length;
  const hasPendingAccessRequests = pendingAccessRequestsCount > 0;
  const linkedDocuments = useMemo<AttachedDocument[]>(() => {
    const seen = new Set<string>();

    const documents: AttachedDocument[] = [];

    declarations.forEach((declaration) => {
      const document = getFirstRelation(declaration.documents);
      if (!document?.id || seen.has(document.id)) return;

      seen.add(document.id);

      const metadata = parseProductDocumentMetadata(document.metadata);
      const fileSource =
        metadata.file_source ??
        (document.storage_path?.startsWith('http') ? 'external_url' : 'file');

      documents.push({
        id: document.id,
        title: document.title || 'Untitled document',
        description: document.description,
        uploaded_at: document.uploaded_at,
        storage_path: document.storage_path,
        source: 'Compliance document',
        category: metadata.document_category ?? null,
        accessLevel: metadata.access_level ?? null,
        expirationDate: metadata.expiration_date ?? null,
        fileSource,
        externalUrl:
          metadata.external_url ??
          (fileSource === 'external_url' ? document.storage_path : null),
        canManage: metadata.managed_in === 'product_details',
        declarationId: declaration.id,
        documentId: document.id,
        kind: 'compliance',
      });
    });

    return documents.sort((left, right) => {
      const leftTime = left.uploaded_at
        ? new Date(left.uploaded_at).getTime()
        : 0;
      const rightTime = right.uploaded_at
        ? new Date(right.uploaded_at).getTime()
        : 0;
      return rightTime - leftTime;
    });
  }, [declarations]);
  const materialWeightPercentageTotal = useMemo(
    () =>
      materials.reduce((sum, material) => sum + material.weight_percentage, 0),
    [materials],
  );
  const isTextile = category?.code === 'TEXTILES_APPAREL';
  const isBattery = category?.code === 'BATTERIES_STORAGE';
  const isIndustrial = category?.code === 'INDUSTRIAL_MACHINERY';
  const isConstruction = category?.code === 'CONSTRUCTION_PRODUCTS';
  const isFurniture = category?.code === 'FURNITURE_MATTRESSES';
  const productMetadata = product?.metadata ?? null;
  const materialsContainWoodOrLeather = useMemo(
    () => containsWoodOrLeatherMaterials(materials),
    [materials],
  );
  const attachedDocuments = useMemo<AttachedDocument[]>(() => {
    const documents: AttachedDocument[] = [...linkedDocuments];

    if (circularity?.repair_manual_url) {
      documents.push({
        id: `repair-manual-${circularity.repair_manual_url}`,
        title: 'Repair Manual',
        description: getFileNameFromPath(circularity.repair_manual_url),
        uploaded_at: null,
        storage_path: circularity.repair_manual_url,
        source: 'Circularity guide',
        category: 'Repair Manual',
        accessLevel: null,
        expirationDate: null,
        fileSource: 'file',
        externalUrl: null,
        canManage: false,
        declarationId: null,
        documentId: null,
        kind: 'guide',
      });
    }

    if (circularity?.disassembly_guide_url) {
      documents.push({
        id: `disassembly-guide-${circularity.disassembly_guide_url}`,
        title: 'Disassembly Guide',
        description: getFileNameFromPath(circularity.disassembly_guide_url),
        uploaded_at: null,
        storage_path: circularity.disassembly_guide_url,
        source: 'Circularity guide',
        category: 'Disassembly Guide',
        accessLevel: null,
        expirationDate: null,
        fileSource: 'file',
        externalUrl: null,
        canManage: false,
        declarationId: null,
        documentId: null,
        kind: 'guide',
      });
    }

    if (productMetadata?.dop_document_path) {
      documents.push({
        id: `construction-dop-${productMetadata.dop_document_path}`,
        title: 'Declaration of Performance (DoP)',
        description: getFileNameFromPath(productMetadata.dop_document_path),
        uploaded_at: null,
        storage_path: productMetadata.dop_document_path,
        source: 'Category-specific document',
        category: 'Construction DoP',
        accessLevel: null,
        expirationDate: null,
        fileSource: 'file',
        externalUrl: null,
        canManage: false,
        declarationId: null,
        documentId: null,
        kind: 'category-specific',
      });
    }

    if (productMetadata?.demolition_guide_url) {
      const demolitionGuideIsExternal =
        productMetadata.demolition_guide_url.startsWith('http');

      documents.push({
        id: `demolition-guide-${productMetadata.demolition_guide_url}`,
        title: 'Demolition Guide',
        description: demolitionGuideIsExternal
          ? productMetadata.demolition_guide_url
          : getFileNameFromPath(productMetadata.demolition_guide_url),
        uploaded_at: null,
        storage_path: productMetadata.demolition_guide_url,
        source: 'Category-specific document',
        category: 'Demolition Guide',
        accessLevel: null,
        expirationDate: null,
        fileSource: demolitionGuideIsExternal ? 'external_url' : 'file',
        externalUrl: demolitionGuideIsExternal
          ? productMetadata.demolition_guide_url
          : null,
        canManage: false,
        declarationId: null,
        documentId: null,
        kind: 'category-specific',
      });
    }

    return documents;
  }, [
    circularity?.disassembly_guide_url,
    circularity?.repair_manual_url,
    linkedDocuments,
    productMetadata?.demolition_guide_url,
    productMetadata?.dop_document_path,
  ]);
  const managedDocuments = useMemo(
    () => linkedDocuments.filter((document) => document.canManage),
    [linkedDocuments],
  );
  const hasReachedManagedDocumentLimit =
    managedDocuments.length >= MANAGED_DOCUMENT_LIMIT;
  const completenessChecks = useMemo<DppCompletenessCheck[]>(() => {
    return buildDppCompletenessChecks({
      productName: product?.name,
      brandName: product?.brand_name,
      skuModel: product?.sku_model,
      categoryName: category?.name,
      categoryCode: category?.code,
      gtinEan: product?.gtin_ean,
      imageUrl: product?.image_url,
      materialsCount: materials.length,
      materialWeightPercentageTotal,
      includeConsumerPackaging: productMetadata?.include_consumer_packaging,
      packagingCount: packaging.length,
      expectedLifetimeYears: circularity?.expected_lifetime_years,
      repairabilityScore: circularity?.repairability_score,
      recyclingInstructions: circularity?.recycling_instructions,
      hasDisassemblyGuideDocument: Boolean(circularity?.disassembly_guide_url),
      hasDopDocument: Boolean(productMetadata?.dop_document_path),
      isTextile,
      isBattery,
      isIndustrial,
      isConstruction,
      isFurniture,
      careSymbolsCount: circularity?.care_symbols?.length ?? 0,
      microplasticsStatus: circularity?.microplastics_status,
      materialsContainWoodOrLeather,
      eudrDdsReference: productMetadata?.eudr_dds_reference,
      flameRetardantsPresent: Boolean(
        productMetadata?.flame_retardants_present,
      ),
      flameRetardantChemicalNamesCount:
        productMetadata?.flame_retardant_chemical_names?.length ?? 0,
      sparePartsAvailabilityYears: circularity?.spare_parts_availability_years,
      ratedCapacityAh: circularity?.rated_capacity_ah,
      ratedEnergyKwh: circularity?.rated_energy_kwh,
      nominalVoltageV: circularity?.nominal_voltage_v,
      minimumVoltageV: circularity?.minimum_voltage_v,
      maximumVoltageV: circularity?.maximum_voltage_v,
      powerCapabilityLimits: circularity?.power_capability_limits,
      roundTripEfficiencyPercent: circularity?.round_trip_efficiency_percent,
      cycleLifetimeDescriptor: circularity?.cycle_lifetime_descriptor,
      operatingTemperatureWindow: circularity?.operating_temperature_window,
      extinguishingAgents: circularity?.extinguishing_agents,
      safetyHandlingMeasures: circularity?.safety_handling_measures,
      softwareSupportYears: circularity?.software_support_years,
    });
  }, [
    isBattery,
    isConstruction,
    isFurniture,
    isIndustrial,
    category?.code,
    category?.name,
    circularity?.care_symbols,
    circularity?.cycle_lifetime_descriptor,
    circularity?.disassembly_guide_url,
    circularity?.expected_lifetime_years,
    circularity?.extinguishing_agents,
    circularity?.maximum_voltage_v,
    circularity?.minimum_voltage_v,
    circularity?.microplastics_status,
    circularity?.nominal_voltage_v,
    circularity?.operating_temperature_window,
    circularity?.power_capability_limits,
    circularity?.rated_capacity_ah,
    circularity?.rated_energy_kwh,
    circularity?.recycling_instructions,
    circularity?.repairability_score,
    circularity?.round_trip_efficiency_percent,
    circularity?.safety_handling_measures,
    circularity?.software_support_years,
    circularity?.spare_parts_availability_years,
    isTextile,
    materialWeightPercentageTotal,
    materialsContainWoodOrLeather,
    materials.length,
    packaging.length,
    product?.brand_name,
    product?.gtin_ean,
    product?.image_url,
    product?.name,
    product?.sku_model,
    productMetadata?.include_consumer_packaging,
    productMetadata?.dop_document_path,
    productMetadata?.eudr_dds_reference,
    productMetadata?.flame_retardants_present,
    productMetadata?.flame_retardant_chemical_names,
  ]);
  const { completedChecks, completenessScore, publicationGaps } = useMemo(
    () => calculateDppCompletenessScore(completenessChecks),
    [completenessChecks],
  );
  const auditEvents = useMemo<AuditEvent[]>(() => {
    if (!product) return [];

    const events: AuditEvent[] = [
      {
        id: `created-${product.id}`,
        timestamp: product.created_at,
        title: 'Product draft created',
        detail: `${product.name} was created in the internal catalog.`,
      },
      {
        id: `updated-${product.id}`,
        timestamp: product.updated_at,
        title: 'Core product metadata was edited',
        detail: 'Core product metadata was edited.',
      },
    ];

    linkedDocuments.forEach((document) => {
      if (!document.uploaded_at) return;

      events.push({
        id: `document-${document.id}`,
        timestamp: document.uploaded_at,
        title: 'Supporting document linked',
        detail:
          document.title || 'Untitled document added to the product record.',
      });
    });

    if (passport?.published_at) {
      events.push({
        id: `published-${passport.id}`,
        timestamp: passport.published_at,
        title: 'Passport published',
        detail:
          'The Digital Product Passport was published to the live endpoint.',
      });
    }

    return events.sort(
      (left, right) =>
        new Date(right.timestamp).getTime() -
        new Date(left.timestamp).getTime(),
    );
  }, [linkedDocuments, passport?.id, passport?.published_at, product]);
  const canPublish = completenessScore === 100 && Boolean(passport);
  const canConfirmPublish =
    publishConfirmDataAccuracy && publishConfirmPublicUrl;
  const previewPayload = useMemo<PublicProductDppPayload | null>(() => {
    if (!product || !category) return null;

    const getDocumentUrl = (document?: ProductDocument | null) => {
      if (!document) return null;
      if (document.storage_path?.startsWith('http')) {
        return document.storage_path;
      }
      return documentUrls[document.id] ?? null;
    };

    const getAttachedDocumentByCategory = (categoryName: string) =>
      attachedDocuments.find(
        (document) => document.category === categoryName,
      ) ?? null;

    const getAttachedDocumentUrl = (document: AttachedDocument | null) => {
      if (!document) return null;
      if (document.externalUrl) return document.externalUrl;
      if (document.storage_path?.startsWith('http')) {
        return document.storage_path;
      }
      return documentUrls[document.id] ?? null;
    };

    const reachDeclarations = declarations.filter(
      (declaration) => declaration.declaration_type === 'REACH_SVHC',
    );
    const rohsDeclaration =
      declarations.find(
        (declaration) => declaration.declaration_type === 'ROHS',
      ) ?? null;
    const popsPfasDeclaration =
      declarations.find(
        (declaration) => declaration.declaration_type === 'POPS_PFAS',
      ) ?? null;
    const microplasticsDeclaration =
      declarations.find(
        (declaration) => declaration.declaration_type === 'MICROPLASTICS',
      ) ?? null;
    const ceDeclarations = declarations.filter(
      (declaration) => declaration.declaration_type === 'CE_DOC',
    );
    const machineClassification =
      productMetadata?.machine_classification ?? null;
    const machineryDeclarationKind =
      machineClassification === 'PARTLY_COMPLETED' ||
      machineClassification === 'PARTLY_COMPLETED_MACHINERY'
        ? 'doi'
        : ceDeclarations.length > 0
          ? 'doc'
          : null;
    const preferredMachineryDocumentId =
      machineryDeclarationKind === 'doi'
        ? (productMetadata?.declaration_of_incorporation_document_id ?? null)
        : (productMetadata?.declaration_of_conformity_document_id ?? null);
    const fallbackMachineryDeclaration = ceDeclarations[0] ?? null;
    const machineryDeclaration = preferredMachineryDocumentId
      ? (ceDeclarations.find(
          (declaration) =>
            declaration.document_id === preferredMachineryDocumentId,
        ) ?? fallbackMachineryDeclaration)
      : fallbackMachineryDeclaration;
    const machineryDocument = getFirstRelation(machineryDeclaration?.documents);
    const publicManagedDocuments = managedDocuments
      .filter((document) => document.accessLevel === 'public')
      .map((document) => ({
        title: document.title,
        description: document.description,
        category: document.category,
        uploadedAt: document.uploaded_at,
        url: getAttachedDocumentUrl(document),
      }))
      .filter((document) => Boolean(document.url));
    const detectedBatteryPfasMaterials = isBattery
      ? getDetectedBatteryPfasMaterials(materials)
      : [];
    const derivedBatteryPfasStatus =
      isBattery &&
      !popsPfasDeclaration &&
      detectedBatteryPfasMaterials.length > 0
        ? 'contains_restricted'
        : !popsPfasDeclaration
          ? ''
          : popsPfasDeclaration.status === 'COMPLIANT'
            ? 'verified_compliant'
            : popsPfasDeclaration.svhc_present ||
                popsPfasDeclaration.present_in_component
              ? 'contains_restricted'
              : 'not_verified';
    const derivedBatteryPfasDetails =
      typeof popsPfasDeclaration?.present_in_component === 'string'
        ? popsPfasDeclaration.present_in_component
        : isBattery && detectedBatteryPfasMaterials.length > 0
          ? detectedBatteryPfasMaterials.join(', ')
          : null;
    const batteryPfasAssessmentNote = isBattery
      ? popsPfasDeclaration
        ? 'Assessment based on declared battery chemistry inputs and published PFAS / POPs evidence.'
        : detectedBatteryPfasMaterials.length > 0
          ? 'Derived from published battery material inputs such as binders, separator coatings, and electrolyte chemistry references.'
          : 'No fluorinated battery materials were detected in the published battery material inputs.'
      : null;
    const productStatus = passport?.status;
    const previewStatus =
      productStatus === 'ARCHIVED' || productStatus === 'WITHDRAWN'
        ? productStatus
        : 'PUBLISHED';

    return {
      productId: product.id,
      passportId: passport?.id ?? `draft-${product.id}`,
      product: {
        name: product.name,
        skuModel: product.sku_model,
        gtinEan: product.gtin_ean,
        brandName: product.brand_name,
        categoryCode: category.code ?? '',
        categoryName: category.name ?? category.code ?? '',
        description: product.description ?? null,
        imageUrl: productImageUrl,
      },
      documents: publicManagedDocuments,
      metadata: {
        productWeightValue: productMetadata?.product_weight_value ?? null,
        productWeightUnit:
          productMetadata?.product_weight_unit === 'kg' ||
          productMetadata?.product_weight_unit === 'g'
            ? productMetadata.product_weight_unit
            : null,
        manufacturerName: productMetadata?.manufacturer_name ?? null,
        manufacturerAddress: productMetadata?.manufacturer_address ?? null,
        importedOutsideEu: Boolean(productMetadata?.imported_outside_eu),
        importerName: productMetadata?.importer_name ?? null,
        importerEmail: productMetadata?.importer_email ?? null,
        importerAddress: productMetadata?.importer_address ?? null,
        seasonCollection: productMetadata?.season_collection ?? null,
        targetAudience: productMetadata?.target_audience ?? [],
        machineClassification,
        ceResponsiblePerson: productMetadata?.ce_responsible_person ?? null,
        ceResponsiblePersonEntityType:
          productMetadata?.ce_responsible_person_entity_type ?? null,
        ceResponsiblePersonCompanyName:
          productMetadata?.ce_responsible_person_company_name ?? null,
        ceResponsiblePersonEuAddress:
          productMetadata?.ce_responsible_person_eu_address ?? null,
        ceResponsiblePersonContactEmail:
          productMetadata?.ce_responsible_person_contact_email ?? null,
        includeConsumerPackaging:
          productMetadata?.include_consumer_packaging ?? packaging.length > 0,
        includeSupplyChainTraceability: Boolean(
          productMetadata?.include_supply_chain_traceability,
        ),
        lifespanUnit:
          productMetadata?.lifespan_unit === 'months' ? 'months' : 'years',
        softwareSupportExpiry: productMetadata?.software_support_expiry ?? null,
        energyEfficiencyRating:
          productMetadata?.energy_efficiency_rating ?? null,
      },
      materials: materials.map((material) => ({
        materialName: material.material_name,
        weightPercentage: material.weight_percentage,
        recycledContentPercentage: material.recycled_content_percentage,
        isRecyclable: material.is_recyclable,
      })),
      packaging: packaging.map((item) => ({
        packagingCategory: item.packaging_category,
        materialType: item.material_type,
        weightGrams: item.weight_grams,
        pcrContentPercent: item.pcr_content_percent,
        recyclabilityStatus: item.recyclability_status,
      })),
      reach: {
        svhcStatus:
          reachDeclarations.length === 0
            ? ''
            : reachDeclarations.some(
                  (declaration) =>
                    declaration.svhc_present ||
                    declaration.substance_name?.trim(),
                )
              ? 'contains'
              : 'none',
        svhcDeclarations: reachDeclarations
          .filter((declaration) => declaration.substance_name?.trim())
          .map((declaration) => ({
            substanceName: declaration.substance_name,
            casEcNumber: declaration.cas_ec_number,
          })),
      },
      rohs: {
        rohsStatus: !rohsDeclaration
          ? ''
          : rohsDeclaration.status === 'EXEMPT'
            ? 'not_applicable'
            : productMetadata?.rohs_exemption_code
              ? 'with_exemption'
              : 'without_exemption',
        exemptionCode: productMetadata?.rohs_exemption_code ?? null,
        customExemptionDetails:
          productMetadata?.custom_exemption_details ?? null,
        exclusionReason:
          rohsDeclaration?.status === 'EXEMPT'
            ? (productMetadata?.rohs_exclusion_reason ?? null)
            : null,
        supportingDocument: (() => {
          const document = getFirstRelation(rohsDeclaration?.documents);
          const url = getDocumentUrl(document);
          return document || url
            ? {
                title: document?.title ?? 'RoHS Supporting Document',
                url,
              }
            : null;
        })(),
      },
      popsPfas: {
        status: derivedBatteryPfasStatus,
        details: derivedBatteryPfasDetails,
        detectedMaterials: detectedBatteryPfasMaterials,
        assessmentNote: batteryPfasAssessmentNote,
        supportingDocument: (() => {
          const document = getFirstRelation(popsPfasDeclaration?.documents);
          const url = getDocumentUrl(document);
          return document || url
            ? {
                title: document?.title ?? 'POPs/PFAS Supporting Document',
                url,
              }
            : null;
        })(),
      },
      machineryDeclaration: machineryDeclarationKind
        ? {
            kind: machineryDeclarationKind,
            title:
              machineryDocument?.title ??
              (machineryDeclarationKind === 'doi'
                ? 'EU Declaration of Incorporation'
                : 'EU Declaration of Conformity'),
            url: getDocumentUrl(machineryDocument),
          }
        : null,
      battery: isBattery
        ? {
            ratedCapacityAh: circularity?.rated_capacity_ah ?? null,
            ratedEnergyKwh: circularity?.rated_energy_kwh ?? null,
            nominalVoltageV: circularity?.nominal_voltage_v ?? null,
            minimumVoltageV: circularity?.minimum_voltage_v ?? null,
            maximumVoltageV: circularity?.maximum_voltage_v ?? null,
            powerCapabilityLimits: circularity?.power_capability_limits ?? null,
            roundTripEfficiencyPercent:
              circularity?.round_trip_efficiency_percent ?? null,
            cycleLifetimeDescriptor:
              circularity?.cycle_lifetime_descriptor ?? null,
            operatingTemperatureWindow:
              circularity?.operating_temperature_window ?? null,
            extinguishingAgents: circularity?.extinguishing_agents ?? null,
            safetyHandlingMeasures:
              circularity?.safety_handling_measures ?? null,
          }
        : null,
      circularity: {
        carbonFootprint: circularity?.carbon_footprint_kg_co2e ?? null,
        expectedLifespan: circularity?.expected_lifetime_years ?? null,
        repairabilityScore: circularity?.repairability_score ?? null,
        repairManual: getAttachedDocumentUrl(
          getAttachedDocumentByCategory('Repair Manual'),
        ),
        disassemblyGuide: getAttachedDocumentUrl(
          getAttachedDocumentByCategory('Disassembly Guide'),
        ),
        microplasticsReport: (() => {
          const document = getFirstRelation(
            microplasticsDeclaration?.documents,
          );
          const url = getDocumentUrl(document);
          return document || url
            ? {
                title: document?.title ?? 'Microplastics Shedding Report',
                url,
              }
            : null;
        })(),
        recyclingInstructions: circularity?.recycling_instructions ?? null,
        waterConsumption: circularity?.water_consumption_liters ?? null,
        careSymbols: circularity?.care_symbols ?? [],
        colorFastnessRating: circularity?.color_fastness_rating ?? null,
        pillingResistanceRating: circularity?.pilling_resistance_rating ?? null,
        sparePartsAvailability: circularity?.spare_parts_availability ?? null,
        takeBackScheme: circularity?.take_back_scheme ?? null,
        microplasticsStatus: circularity?.microplastics_status ?? null,
      },
      categorySpecific: {
        construction: isConstruction
          ? {
              vocEmissionClass: productMetadata?.voc_emission_class ?? null,
              dopDocument: (() => {
                const document =
                  getAttachedDocumentByCategory('Construction DoP');
                const url = getAttachedDocumentUrl(document);
                return document || url
                  ? {
                      title:
                        document?.title ?? 'Declaration of Performance (DoP)',
                      url,
                    }
                  : null;
              })(),
              demolitionGuide: (() => {
                const document =
                  getAttachedDocumentByCategory('Demolition Guide');
                const url = getAttachedDocumentUrl(document);
                return document || url
                  ? {
                      title: document?.title ?? 'Demolition Guide',
                      url,
                    }
                  : null;
              })(),
            }
          : null,
        furniture: isFurniture
          ? {
              eudrDdsReference: productMetadata?.eudr_dds_reference ?? null,
              flameRetardantsPresent: Boolean(
                productMetadata?.flame_retardants_present,
              ),
              flameRetardantChemicalNames:
                productMetadata?.flame_retardant_chemical_names ?? [],
              hasRemovableCovers: productMetadata?.has_removable_covers ?? null,
            }
          : null,
        metals:
          category?.code === 'IRON_STEEL_METALS'
            ? {
                productionRoute: productMetadata?.production_route ?? null,
                cbamEmbeddedEmissions:
                  productMetadata?.cbam_embedded_emissions ?? null,
              }
            : null,
        tires:
          category?.code === 'TIRES_AUTOMOTIVE'
            ? {
                rollingResistanceClass:
                  productMetadata?.rolling_resistance_class ?? null,
                wetGripClass: productMetadata?.wet_grip_class ?? null,
                abrasionRateMgKm: productMetadata?.abrasion_rate_mg_km ?? null,
                isRetreadable: productMetadata?.is_retreadable ?? null,
              }
            : null,
        chemicals:
          category?.code === 'CHEMICALS'
            ? {
                ufiCode: productMetadata?.ufi_code ?? null,
                clpHazardPhrases: productMetadata?.clp_hazard_phrases ?? [],
                surfactantBiodegradabilityPercentage:
                  productMetadata?.surfactant_biodegradability_percentage ??
                  null,
              }
            : null,
      },
      passport: {
        status: previewStatus,
        statusReason: null,
        statusUpdatedAt: null,
        uniqueProductIdentifier:
          passport?.unique_product_identifier ?? product.gtin_ean ?? product.id,
        publicUrl: passport?.public_url ?? null,
        qrCodeAssetUrl: passport?.qr_code_asset_url ?? null,
        publishedAt: passport?.published_at ?? null,
        visibilitySettings: {
          technicalSpecsAccess:
            passport?.visibility_settings?.technical_specs_access ===
            'restricted'
              ? 'restricted'
              : 'public',
        },
      },
      access: {
        grantedTier: 'OPEN',
        hasExtendedAccess: false,
        hasDocumentAccess: false,
        tokenHolderEmail: null,
      },
      extendedData: null,
    };
  }, [
    attachedDocuments,
    category,
    declarations,
    managedDocuments,
    documentUrls,
    isBattery,
    isConstruction,
    isFurniture,
    materials,
    packaging,
    passport,
    product,
    productImageUrl,
    productMetadata,
    circularity,
  ]);

  const openPreviewModal = useCallback(() => {
    if (!canPublish || !previewPayload) return;
    setShowPreviewModal(true);
  }, [canPublish, previewPayload]);

  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
  }, []);

  useEffect(() => {
    const resolveDocumentUrls = async () => {
      if (attachedDocuments.length === 0) {
        setDocumentUrls({});
        return;
      }

      const entries = await Promise.all(
        attachedDocuments.map(async (document) => {
          if (!document.storage_path) return [document.id, ''] as const;
          if (document.storage_path.startsWith('http')) {
            return [document.id, document.storage_path] as const;
          }

          const { data } = await supabase.storage
            .from('documents')
            .createSignedUrl(document.storage_path, 60 * 5);

          return [document.id, data?.signedUrl ?? ''] as const;
        }),
      );

      setDocumentUrls(
        Object.fromEntries(entries.filter(([, value]) => Boolean(value))),
      );
    };

    resolveDocumentUrls();
  }, [attachedDocuments]);

  useEffect(() => {
    const resolveProductImageUrl = async () => {
      if (!product?.image_url) {
        setProductImageUrl(null);
        return;
      }

      if (product.image_url.startsWith('http')) {
        setProductImageUrl(product.image_url);
        return;
      }

      const { data } = await supabase.storage
        .from('documents')
        .createSignedUrl(product.image_url, 60 * 5);

      setProductImageUrl(data?.signedUrl ?? null);
    };

    void resolveProductImageUrl();
  }, [product?.image_url]);

  const openImagePicker = () => {
    setImageActionError(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
      imageInputRef.current.click();
    }
  };

  const handleProductImageSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !productId) return;

    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      setImageActionError('Your organisation session is not available.');
      return;
    }

    setImageActionError(null);
    const oldImagePath = product?.image_url ?? null;
    const uploadResult = await uploadProductImage(
      file,
      organisationId,
      user.id,
    );

    if (!uploadResult) {
      return;
    }

    const { error: updateError } = await supabase
      .from('products')
      .update({ image_url: uploadResult.storagePath })
      .eq('id', productId);

    if (updateError) {
      setImageActionError(updateError.message);
      return;
    }

    if (
      oldImagePath &&
      oldImagePath !== uploadResult.storagePath &&
      !oldImagePath.startsWith('http')
    ) {
      const [storageCleanupResult, documentCleanupResult] = await Promise.all([
        supabase.storage.from('documents').remove([oldImagePath]),
        supabase
          .from('documents')
          .delete()
          .eq('object_id', organisationId)
          .eq('storage_path', oldImagePath),
      ]);

      if (storageCleanupResult.error || documentCleanupResult.error) {
        setImageActionError(
          'Image updated, but the previous thumbnail could not be fully removed.',
        );
      }
    }

    await loadProduct();
  };

  const openAddDocumentModal = () => {
    if (hasReachedManagedDocumentLimit) {
      setDocumentActionError(
        `You can attach up to ${MANAGED_DOCUMENT_LIMIT} manual documents to a product.`,
      );
      setDocumentActionSuccess(null);
      return;
    }

    setEditingDocument(null);
    setDocumentForm(initialComplianceDocumentForm);
    setDocumentActionError(null);
    setDocumentActionSuccess(null);
    setDocumentDropActive(false);
    if (complianceDocumentInputRef.current) {
      complianceDocumentInputRef.current.value = '';
    }
    setShowDocumentModal(true);
  };

  const openEditDocumentModal = (document: AttachedDocument) => {
    if (!document.canManage) return;

    setEditingDocument(document);
    setDocumentForm({
      category:
        (document.category as ComplianceDocumentCategory | null) ?? 'Other',
      title: document.title,
      fileSource: document.fileSource,
      accessLevel: document.accessLevel ?? 'restricted',
      expirationDate: document.expirationDate ?? '',
      externalUrl: document.externalUrl ?? '',
      file: null,
    });
    setDocumentActionError(null);
    setDocumentActionSuccess(null);
    setDocumentDropActive(false);
    if (complianceDocumentInputRef.current) {
      complianceDocumentInputRef.current.value = '';
    }
    setShowDocumentModal(true);
  };

  const closeDocumentModal = () => {
    if (documentSaving || documentUploading) return;

    setShowDocumentModal(false);
    setEditingDocument(null);
    setDocumentForm(initialComplianceDocumentForm);
    setDocumentActionError(null);
    setDocumentDropActive(false);
    if (complianceDocumentInputRef.current) {
      complianceDocumentInputRef.current.value = '';
    }
  };

  const setDocumentFormValue = <Key extends keyof ComplianceDocumentFormState>(
    key: Key,
    value: ComplianceDocumentFormState[Key],
  ) => {
    setDocumentActionError(null);
    setDocumentForm((current) => ({ ...current, [key]: value }));
  };

  const handleComplianceFileSelected = (file: File | null) => {
    setDocumentActionError(null);

    if (!file) {
      setDocumentFormValue('file', null);
      return;
    }

    if (file.size > complianceDocumentMaxBytes) {
      setDocumentActionError('File size must be 25MB or less.');
      return;
    }

    setDocumentForm((current) => ({
      ...current,
      title: current.title || file.name.replace(/\.[^.]+$/, ''),
      file,
    }));
  };

  const buildManagedDocumentMetadata = (): ProductDocumentMetadata => ({
    managed_in: 'product_details',
    document_category: documentForm.category,
    access_level: documentForm.accessLevel,
    expiration_date: documentForm.expirationDate || null,
    file_source: documentForm.fileSource,
    external_url:
      documentForm.fileSource === 'external_url'
        ? documentForm.externalUrl.trim()
        : null,
  });

  const createManagedDocument = async () => {
    const organisationId = selectedOrganisation?.organisation?.id;
    if (!organisationId || !user?.id) {
      throw new Error('Your organisation session is not available.');
    }

    const metadata = buildManagedDocumentMetadata();

    if (documentForm.fileSource === 'file') {
      if (!documentForm.file) {
        throw new Error('Select a file to upload.');
      }

      const uploadResult = await uploadComplianceDocument(
        documentForm.file,
        organisationId,
        user.id,
      );

      if (!uploadResult) {
        throw new Error('Document upload failed.');
      }

      const { error: documentUpdateError } = await supabase
        .from('documents')
        .update({
          title: documentForm.title.trim(),
          description: null,
          storage_path: uploadResult.storagePath,
          metadata,
        })
        .eq('id', uploadResult.documentId)
        .eq('object_id', organisationId);

      if (documentUpdateError) {
        throw documentUpdateError;
      }

      return { documentId: uploadResult.documentId };
    }

    const { data, error: documentInsertError } = await supabase
      .from('documents')
      .insert({
        object_id: organisationId,
        title: documentForm.title.trim(),
        description: null,
        storage_path: documentForm.externalUrl.trim(),
        uploaded_by: user.id,
        metadata,
      })
      .select('id')
      .single();

    if (documentInsertError) {
      throw documentInsertError;
    }

    return { documentId: data.id as string };
  };

  const removeManagedDocumentRecord = async (documentId: string) => {
    const { data, error: deleteError } = await supabase.functions.invoke(
      'delete-document',
      {
        body: { documentId },
      },
    );

    if (deleteError) {
      throw deleteError;
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    if (parsed?.success === false) {
      throw new Error(parsed.error || 'Unable to delete document.');
    }
  };

  const handleSaveComplianceDocument = async () => {
    if (!productId) return;

    if (!editingDocument && hasReachedManagedDocumentLimit) {
      setDocumentActionError(
        `You can attach up to ${MANAGED_DOCUMENT_LIMIT} manual documents to a product.`,
      );
      return;
    }

    const title = documentForm.title.trim();
    if (!title) {
      setDocumentActionError('Document title is required.');
      return;
    }

    if (documentForm.fileSource === 'external_url') {
      const urlValue = documentForm.externalUrl.trim();
      if (!urlValue) {
        setDocumentActionError('External URL is required.');
        return;
      }

      try {
        new URL(urlValue);
      } catch {
        setDocumentActionError('Enter a valid external URL.');
        return;
      }
    }

    if (
      documentForm.fileSource === 'file' &&
      !documentForm.file &&
      (!editingDocument || editingDocument.fileSource !== 'file')
    ) {
      setDocumentActionError('Select a file to upload.');
      return;
    }

    setDocumentSaving(true);
    setDocumentActionError(null);
    setDocumentActionSuccess(null);

    try {
      if (
        editingDocument?.canManage &&
        editingDocument.documentId &&
        editingDocument.declarationId
      ) {
        const switchingSource =
          editingDocument.fileSource !== documentForm.fileSource;
        const replacingFile =
          documentForm.fileSource === 'file' && Boolean(documentForm.file);

        if (!switchingSource && !replacingFile) {
          const { error: updateError } = await supabase
            .from('documents')
            .update({
              title,
              storage_path:
                documentForm.fileSource === 'external_url'
                  ? documentForm.externalUrl.trim()
                  : editingDocument.storage_path,
              metadata: buildManagedDocumentMetadata(),
            })
            .eq('id', editingDocument.documentId);

          if (updateError) {
            throw updateError;
          }
        } else {
          const nextDocument = await createManagedDocument();

          const { error: declarationUpdateError } = await supabase
            .from('product_declarations')
            .update({ document_id: nextDocument.documentId })
            .eq('id', editingDocument.declarationId)
            .eq('product_id', productId);

          if (declarationUpdateError) {
            throw declarationUpdateError;
          }

          await removeManagedDocumentRecord(editingDocument.documentId);
        }
      } else {
        const nextDocument = await createManagedDocument();

        const { error: declarationInsertError } = await supabase
          .from('product_declarations')
          .insert({
            product_id: productId,
            declaration_type: 'CE_DOC',
            status: 'UNDER_REVIEW',
            svhc_present: null,
            substance_name: null,
            cas_ec_number: null,
            present_in_component: null,
            scip_number: null,
            document_id: nextDocument.documentId,
          });

        if (declarationInsertError) {
          throw declarationInsertError;
        }
      }

      await loadProduct();
      setDocumentActionSuccess(
        editingDocument ? 'Document updated.' : 'Document added.',
      );
      closeDocumentModal();
    } catch (saveError) {
      setDocumentActionError(
        saveError instanceof Error
          ? saveError.message
          : 'Unable to save compliance document.',
      );
    } finally {
      setDocumentSaving(false);
    }
  };

  const handleDeleteComplianceDocument = async (document: AttachedDocument) => {
    if (
      !document.canManage ||
      !document.documentId ||
      !document.declarationId ||
      !productId
    ) {
      return;
    }

    if (!confirm('Delete this compliance document?')) {
      return;
    }

    setDocumentSaving(true);
    setDocumentActionError(null);
    setDocumentActionSuccess(null);

    try {
      const { error: declarationDeleteError } = await supabase
        .from('product_declarations')
        .delete()
        .eq('id', document.declarationId)
        .eq('product_id', productId);

      if (declarationDeleteError) {
        throw declarationDeleteError;
      }

      await removeManagedDocumentRecord(document.documentId);
      await loadProduct();
      setDocumentActionSuccess('Document deleted.');
    } catch (deleteError) {
      setDocumentActionError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Unable to delete compliance document.',
      );
    } finally {
      setDocumentSaving(false);
    }
  };

  const closeSectionDrawer = () => {
    if (sectionSaving) return;
    setActiveSection(null);
    setSectionError(null);
  };

  const resolveCategoryId = async (categoryCode: string) => {
    if (!categoryCode) throw new Error('Select a product category.');

    const { data, error: categoryLookupError } = await supabase
      .from('product_categories')
      .select('id')
      .eq('code', categoryCode)
      .single();

    if (categoryLookupError) throw categoryLookupError;
    return data.id as string;
  };

  //   const openSectionDrawer = (section: Exclude<EditableSection, null>) => {
  //     if (!product) return;

  //     setSectionError(null);

  //     if (section === 'basic-info') {
  //       setBasicInfoForm({
  //         name: product.name,
  //         skuModel: product.sku_model,
  //         brandName: product.brand_name,
  //         gtinEan: product.gtin_ean ?? '',
  //         categoryCode: category?.code ?? '',
  //         description: product.description ?? '',
  //       });
  //     }

  //     if (section === 'bom-svhc') {
  //       const reachDeclarations = declarations.filter(
  //         (declaration) => declaration.declaration_type === 'REACH_SVHC',
  //       );

  //       setBomSvhcForm({
  //         materials:
  //           materials.length > 0
  //             ? materials.map((material) => ({
  //                 material_name: material.material_name,
  //                 weight_percentage: toEditableString(material.weight_percentage),
  //                 recycled_content_percentage: toEditableString(
  //                   material.recycled_content_percentage,
  //                 ),
  //                 is_recyclable: material.is_recyclable,
  //               }))
  //             : [
  //                 {
  //                   material_name: '',
  //                   weight_percentage: '',
  //                   recycled_content_percentage: '0',
  //                   is_recyclable: false,
  //                 },
  //               ],
  //         svhcStatus:
  //           reachDeclarations.length === 0
  //             ? ''
  //             : reachDeclarations.some(
  //                   (declaration) =>
  //                     declaration.svhc_present ||
  //                     declaration.substance_name?.trim(),
  //                 )
  //               ? 'contains'
  //               : 'none',
  //         svhcDeclarations: reachDeclarations
  //           .filter((declaration) => declaration.substance_name?.trim())
  //           .map((declaration) => ({
  //             substance_name: declaration.substance_name ?? '',
  //             cas_ec_number: declaration.cas_ec_number ?? '',
  //             present_in_component: declaration.present_in_component ?? '',
  //             scip_number: declaration.scip_number ?? '',
  //             document_id: declaration.document_id ?? null,
  //           })),
  //       });
  //     }

  //     if (section === 'packaging') {
  //       setPackagingForm({
  //         packagingItems:
  //           packaging.length > 0
  //             ? packaging.map((item) => ({
  //                 packaging_category: item.packaging_category,
  //                 material_type: item.material_type,
  //                 weight_grams: toEditableString(item.weight_grams),
  //                 pcr_content_percent: toEditableString(item.pcr_content_percent),
  //                 recyclability_status: item.recyclability_status ?? '',
  //               }))
  //             : [
  //                 {
  //                   packaging_category: '',
  //                   material_type: '',
  //                   weight_grams: '',
  //                   pcr_content_percent: '',
  //                   recyclability_status: '',
  //                 },
  //               ],
  //         packagingEprRegistration:
  //           packaging.find((item) => item.epr_registration_number)
  //             ?.epr_registration_number ?? '',
  //       });
  //     }

  //     if (section === 'eco-specs') {
  //       setEcoSpecsForm({
  //         carbonFootprint: toEditableString(
  //           circularity?.carbon_footprint_kg_co2e,
  //         ),
  //         expectedLifetimeYears: toEditableString(
  //           circularity?.expected_lifetime_years,
  //         ),
  //         repairabilityScore: toEditableString(circularity?.repairability_score),
  //         repairGuideUrl:
  //           circularity?.repair_manual_url ||
  //           circularity?.disassembly_guide_url ||
  //           '',
  //         recyclingInstructions: circularity?.recycling_instructions ?? '',
  //         waterConsumption: toEditableString(
  //           circularity?.water_consumption_liters,
  //         ),
  //         colorFastnessRating: toEditableString(
  //           circularity?.color_fastness_rating,
  //         ),
  //         pillingResistanceRating: toEditableString(
  //           circularity?.pilling_resistance_rating,
  //         ),
  //         sparePartsAvailability: circularity?.spare_parts_availability ?? '',
  //         takeBackScheme: circularity?.take_back_scheme ?? '',
  //         microplasticsStatus:
  //           circularity?.microplastics_status === 'not_applicable' ||
  //           circularity?.microplastics_status === 'untested' ||
  //           circularity?.microplastics_status === 'tested'
  //             ? circularity.microplastics_status
  //             : '',
  //         eprRegistrationNumber: circularity?.epr_registration_number ?? '',
  //         careSymbols: circularity?.care_symbols?.join(', ') ?? '',
  //       });
  //     }

  //     setActiveSection(section);
  //   };

  const handleSaveSection = async () => {
    if (!productId) return;

    setSectionSaving(true);
    setSectionError(null);

    try {
      if (activeSection === 'basic-info') {
        const categoryId = await resolveCategoryId(basicInfoForm.categoryCode);
        const { error: updateError } = await supabase
          .from('products')
          .update({
            name: basicInfoForm.name.trim(),
            sku_model: basicInfoForm.skuModel.trim(),
            brand_name: basicInfoForm.brandName.trim(),
            gtin_ean: basicInfoForm.gtinEan.trim() || null,
            category_id: categoryId,
            description: basicInfoForm.description.trim() || null,
          })
          .eq('id', productId);
        if (updateError) throw updateError;
      }

      if (activeSection === 'bom-svhc') {
        const filteredMaterials = bomSvhcForm.materials.filter((material) =>
          material.material_name.trim(),
        );
        const materialTotal = filteredMaterials.reduce(
          (sum, material) => sum + (Number(material.weight_percentage) || 0),
          0,
        );

        if (materialTotal > 100) {
          throw new Error('Total material breakdown cannot exceed 100%.');
        }

        if (
          bomSvhcForm.svhcStatus === 'contains' &&
          !bomSvhcForm.svhcDeclarations.some((item) =>
            item.substance_name.trim(),
          )
        ) {
          throw new Error(
            'Add at least one SVHC declaration with a substance name.',
          );
        }

        const deleteResults = await Promise.all([
          supabase
            .from('product_materials')
            .delete()
            .eq('product_id', productId),
          supabase
            .from('product_declarations')
            .delete()
            .eq('product_id', productId)
            .eq('declaration_type', 'REACH_SVHC'),
        ]);
        const deleteError = deleteResults.find((result) => result.error)?.error;
        if (deleteError) throw deleteError;

        if (filteredMaterials.length > 0) {
          const { error: materialsError } = await supabase
            .from('product_materials')
            .insert(
              filteredMaterials.map((material) => ({
                product_id: productId,
                material_name: material.material_name.trim(),
                weight_percentage: Number(material.weight_percentage) || 0,
                recycled_content_percentage:
                  asOptionalNumber(material.recycled_content_percentage) ?? 0,
                is_recyclable: material.is_recyclable,
              })),
            );
          if (materialsError) throw materialsError;
        }

        const svhcDeclarations =
          bomSvhcForm.svhcStatus === 'contains'
            ? bomSvhcForm.svhcDeclarations
                .filter((item) => item.substance_name.trim())
                .map((item) => ({
                  product_id: productId,
                  declaration_type: 'REACH_SVHC',
                  status: 'UNDER_REVIEW',
                  svhc_present: true,
                  substance_name: item.substance_name.trim(),
                  cas_ec_number: item.cas_ec_number.trim() || null,
                  present_in_component:
                    item.present_in_component.trim() || null,
                  scip_number: item.scip_number.trim() || null,
                  document_id: item.document_id,
                }))
            : bomSvhcForm.svhcStatus === 'none'
              ? [
                  {
                    product_id: productId,
                    declaration_type: 'REACH_SVHC',
                    status: 'COMPLIANT',
                    svhc_present: false,
                    substance_name: null,
                    cas_ec_number: null,
                    present_in_component: null,
                    scip_number: null,
                    document_id:
                      declarations.find(
                        (item) => item.declaration_type === 'REACH_SVHC',
                      )?.document_id ?? null,
                  },
                ]
              : [];

        if (svhcDeclarations.length > 0) {
          const { error: svhcError } = await supabase
            .from('product_declarations')
            .insert(svhcDeclarations);
          if (svhcError) throw svhcError;
        }
      }

      if (activeSection === 'packaging') {
        const { error: deletePackagingError } = await supabase
          .from('product_packaging')
          .delete()
          .eq('product_id', productId);
        if (deletePackagingError) throw deletePackagingError;

        const filteredPackaging = packagingForm.packagingItems.filter(
          (item) => item.packaging_category && item.material_type,
        );

        if (filteredPackaging.length > 0) {
          const { error: insertPackagingError } = await supabase
            .from('product_packaging')
            .insert(
              filteredPackaging.map((item) => ({
                product_id: productId,
                packaging_category: item.packaging_category,
                material_type: item.material_type,
                weight_grams: asOptionalNumber(item.weight_grams) ?? 0,
                pcr_content_percent:
                  asOptionalNumber(item.pcr_content_percent) ?? 0,
                recyclability_status: item.recyclability_status || null,
                epr_registration_number:
                  packagingForm.packagingEprRegistration.trim() || null,
              })),
            );
          if (insertPackagingError) throw insertPackagingError;
        }
      }

      if (activeSection === 'eco-specs') {
        const { error: circularityError } = await supabase
          .from('product_circularity')
          .upsert(
            {
              product_id: productId,
              carbon_footprint_kg_co2e: asOptionalNumber(
                ecoSpecsForm.carbonFootprint,
              ),
              expected_lifetime_years: asOptionalNumber(
                ecoSpecsForm.expectedLifetimeYears,
              ),
              repairability_score: asOptionalNumber(
                ecoSpecsForm.repairabilityScore,
              ),
              repair_manual_url: ecoSpecsForm.repairGuideUrl.trim() || null,
              disassembly_guide_url: ecoSpecsForm.repairGuideUrl.trim() || null,
              recycling_instructions:
                ecoSpecsForm.recyclingInstructions.trim() || null,
              water_consumption_liters: asOptionalNumber(
                ecoSpecsForm.waterConsumption,
              ),
              color_fastness_rating: asOptionalNumber(
                ecoSpecsForm.colorFastnessRating,
              ),
              pilling_resistance_rating: asOptionalNumber(
                ecoSpecsForm.pillingResistanceRating,
              ),
              spare_parts_availability:
                ecoSpecsForm.sparePartsAvailability.trim() || null,
              take_back_scheme: ecoSpecsForm.takeBackScheme.trim() || null,
              microplastics_status: ecoSpecsForm.microplasticsStatus || null,
              epr_registration_number:
                ecoSpecsForm.eprRegistrationNumber.trim() || null,
              care_symbols: ecoSpecsForm.careSymbols.trim()
                ? ecoSpecsForm.careSymbols
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean)
                : null,
            },
            { onConflict: 'product_id' },
          );
        if (circularityError) throw circularityError;
      }

      await loadProduct();
      setActiveSection(null);
    } catch (saveError) {
      setSectionError(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save product section.',
      );
    } finally {
      setSectionSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">Error loading product: {error}</Alert>;
  }

  if (!product) {
    return <Alert variant="warning">Product not found.</Alert>;
  }

  return (
    <Container fluid className="px-0 product-details-page">
      <style>
        {`
          .product-details-hero {
            background: linear-gradient(135deg, #f7f4ec 0%, #ffffff 62%, #edf6f2 100%);
            border: 1px solid rgba(28, 52, 43, 0.08);
          }

          .product-details-image {
            width: 88px;
            height: 88px;
            border-radius: 1.25rem;
            object-fit: cover;
            background: linear-gradient(135deg, #f0ede4 0%, #dfece5 100%);
            border: 1px solid rgba(28, 52, 43, 0.08);
          }

          .product-details-image-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #385246;
            font-size: 1.75rem;
          }

          .product-details-image-shell {
            position: relative;
            width: 88px;
            height: 88px;
            flex: 0 0 auto;
          }

          .product-details-image-action {
            position: absolute;
            right: 0.35rem;
            bottom: 0.35rem;
          }

          .product-details-tab-card,
          .product-details-hero,
          .product-details-banner {
            border-radius: 1.25rem;
          }

          .product-details-banner {
            background: linear-gradient(135deg, #fff7df 0%, #fffdf7 100%);
            border: 1px solid rgba(169, 120, 0, 0.18);
          }

          .product-details-tabs .nav-link {
            color: #385246;
            font-weight: 600;
          }

          .product-details-tabs .nav-link.active {
            color: #17392e;
          }

          .product-details-mini-card {
            border-radius: 1rem;
            border: 1px solid rgba(28, 52, 43, 0.08);
            background: #ffffff;
          }

          .product-details-public-preview {
            background: linear-gradient(180deg, #18392e 0%, #1f4a3b 100%);
            color: #f6f5f0;
            border-radius: 1.25rem;
            overflow: hidden;
          }

          .product-details-drawer {
            --bs-offcanvas-width: min(48vw, 880px);
          }

          @media (max-width: 991.98px) {
            .product-details-drawer {
              --bs-offcanvas-width: 100vw;
            }
          }
        `}
      </style>

      <Card className="product-details-hero shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-start gap-3">
                <div className="w-100">
                  <Link
                    to="/products"
                    className="text-decoration-none small fw-semibold"
                  >
                    <i className="bi bi-arrow-left me-2" />
                    Products
                  </Link>
                </div>
                <div className="d-flex flex-wrap align-items-start gap-3">
                  <div className="product-details-image-shell">
                    {productImageUrl ? (
                      <img
                        src={productImageUrl}
                        alt={product.name}
                        className="product-details-image"
                      />
                    ) : (
                      <div className="product-details-image product-details-image-placeholder">
                        <i className="bi bi-box-seam" />
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="light"
                      className="product-details-image-action border shadow-sm"
                      onClick={openImagePicker}
                      disabled={imageUploading}
                    >
                      {imageUploading ? (
                        <Spinner size="sm" animation="border" />
                      ) : productImageUrl ? (
                        'Edit'
                      ) : (
                        'Add'
                      )}
                    </Button>
                    <Form.Control
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProductImageSelected}
                      className="d-none"
                    />
                  </div>
                  <div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <h1 className="h3 mb-0">{product.name}</h1>
                      <Badge bg="light" text="dark" className="border">
                        {category?.name || category?.code || 'Uncategorized'}
                      </Badge>
                      <Badge bg={productStatusBadge} className="text-uppercase">
                        {productStatus}
                      </Badge>
                    </div>
                    <div className="text-muted mb-2">
                      SKU / Model: {product.sku_model}
                    </div>
                    <div className="d-flex flex-wrap gap-3 small text-muted">
                      <span>Brand: {product.brand_name}</span>
                      <span>Supplier: {supplier?.company_name || '-'}</span>
                      <span>GTIN / EAN: {product.gtin_ean || '-'}</span>
                    </div>
                    {publishedDppUrl && (
                      <div className="mt-1 d-flex flex-wrap align-items-center gap-2">
                        <strong>Public Passport URL:</strong>
                        <a
                          href={publishedDppUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {publishedDppUrl}
                        </a>
                        <Button
                          variant="link"
                          onClick={() => void copyPublicDppUrl()}
                        >
                          <i className="bi bi-clipboard me-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4">
                <Button
                  variant="outline-dark"
                  onClick={() => navigate(`/products/${product.id}/edit`)}
                >
                  Edit Product
                </Button>
                {!isPublished && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      !canPublish ? (
                        <Tooltip id="preview-passport-disabled-tooltip">
                          Prevew is available when passport is ready to be
                          published. Please complete setting up the product.
                        </Tooltip>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <span className="d-inline-flex">
                      <Button
                        variant="outline-primary"
                        disabled={!canPublish}
                        onClick={openPreviewModal}
                      >
                        Preview Passport
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
                {isPublished && (
                  <Button variant="warning" onClick={openLifecycleModal}>
                    Manage Lifecycle
                  </Button>
                )}
                {!isPublished && (
                  <Button
                    variant="primary"
                    disabled={!canPublish || publishing}
                    onClick={openPublishConfirmModal}
                  >
                    {publishing ? (
                      <>
                        <Spinner
                          size="sm"
                          animation="border"
                          className="me-2"
                        />
                        Publishing...
                      </>
                    ) : (
                      'Publish Passport'
                    )}
                  </Button>
                )}
              </div>
            </div>
            <div className="d-flex flex-column align-items-end gap-3">
              {publishedDppUrl && (
                <div className="d-flex flex-column align-items-end gap-2 text-end">
                  <strong>Public Passport QR Code</strong>
                  <div
                    ref={qrCodeContainerRef}
                    className="rounded-4 border bg-white p-3 shadow-sm"
                  >
                    <QRCodeSVG
                      value={publishedDppUrl}
                      size={160}
                      level="H"
                      includeMargin
                    />
                  </div>
                  <div className="w-100 d-flex justify-content-center">
                    <DropdownButton
                      variant="outline-secondary"
                      title="Download QR code"
                      align="end"
                    >
                      <Dropdown.Item onClick={downloadQrCodeAsSvg}>
                        Download as SVG
                      </Dropdown.Item>
                      <Dropdown.Item onClick={downloadQrCodeAsPng}>
                        Download as PNG
                      </Dropdown.Item>
                    </DropdownButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {(imageActionError || imageUploadError) && (
        <Alert variant="warning" className="mb-4">
          {imageActionError || imageUploadError}
        </Alert>
      )}

      {publishMessage && (
        <Alert variant="success" className="mb-4">
          {publishMessage}
        </Alert>
      )}

      {(isArchived || isWithdrawn) && (
        <Alert variant={isWithdrawn ? 'danger' : 'warning'} className="mb-4">
          This product is {isWithdrawn ? 'withdrawn' : 'discontinued'}. You can
          publish it again.
        </Alert>
      )}

      {isDraft && (
        <Alert className="product-details-banner mb-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
            <div>
              <div className="text-uppercase small fw-semibold text-muted mb-1">
                Draft Readiness
              </div>
              <div className="h4 mb-2">
                DPP completeness: {completenessScore}%
              </div>
              <div className="text-muted">
                {publicationGaps.length === 0
                  ? 'This draft is ready for publication.'
                  : 'Complete the remaining gaps below to reach 100% publication readiness.'}
              </div>
            </div>
            <div className="flex-grow-1" style={{ minWidth: '280px' }}>
              {publicationGaps.length > 0 && (
                <ul className="mb-0 ps-3">
                  {publicationGaps.map((gap) => (
                    <li key={gap} className="mb-1">
                      {gap}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Alert>
      )}

      <Tabs
        defaultActiveKey="overview"
        id="product-details-tabs"
        className="mb-4 product-details-tabs"
      >
        <Tab eventKey="overview" title="Overview & Readiness">
          <Row className="g-4 mt-1">
            <Col xl={6}>
              <Card className="product-details-tab-card h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <div className="text-uppercase small fw-semibold text-muted">
                        Completeness Checklist
                      </div>
                      <div className="h5 mb-0 mt-1">
                        {completedChecks} / {completenessChecks.length}
                      </div>
                    </div>
                    <Badge
                      bg={completenessScore === 100 ? 'success' : 'warning'}
                      text={completenessScore === 100 ? undefined : 'dark'}
                    >
                      {completenessScore}%
                    </Badge>
                  </div>
                  <ListGroup variant="flush">
                    {completenessChecks.map((check) => (
                      <ListGroup.Item
                        key={check.key}
                        className="px-0 d-flex justify-content-between gap-3"
                      >
                        <div>
                          <div className="fw-semibold">{check.label}</div>
                          {!check.complete && (
                            <div className="small text-muted">{check.gap}</div>
                          )}
                        </div>
                        <i
                          className={`bi ${check.complete ? 'bi-check-circle-fill text-success' : 'bi-exclamation-circle-fill text-warning'}`}
                        />
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={6}>
              <Row className="g-3">
                <Col sm={6} xl={12}>
                  <Card className="product-details-mini-card shadow-sm">
                    <Card.Body>
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Carbon Footprint
                      </div>
                      <div className="h4 mb-0">
                        {formatNumber(
                          circularity?.carbon_footprint_kg_co2e,
                          ' kg CO2e',
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} xl={12}>
                  <Card className="product-details-mini-card shadow-sm">
                    <Card.Body>
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Expected Lifetime
                      </div>
                      <div className="h4 mb-0">
                        {formatNumber(
                          circularity?.expected_lifetime_years,
                          ' years',
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} xl={12}>
                  <Card className="product-details-mini-card shadow-sm">
                    <Card.Body>
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Repairability
                      </div>
                      <div className="h4 mb-0">
                        {formatNumber(
                          circularity?.repairability_score,
                          ' / 10',
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} xl={12}>
                  <Card className="product-details-mini-card shadow-sm">
                    <Card.Body>
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Attached Documents
                      </div>
                      <div className="h4 mb-0">{attachedDocuments.length}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Tab>
        {/* 
        <Tab eventKey="passport-data" title="Passport Data">
          <Card className="product-details-tab-card shadow-sm mt-3">
            <Card.Body>
              <Accordion alwaysOpen>
                <Accordion.Item eventKey="identity">
                  <Accordion.Header>Basic Info</Accordion.Header>
                  <Accordion.Body>
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openSectionDrawer('basic-info')}
                      >
                        <i className="bi bi-pencil-square me-1" />
                        Edit Section
                      </Button>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="small text-muted">Product ID</div>
                        <div className="text-break">{product.id}</div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">Supplier</div>
                        <div>{supplier?.company_name || '-'}</div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">Category</div>
                        <div>{category?.name || category?.code || '-'}</div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">
                          Passport visibility
                        </div>
                        <div>
                          {titleCase(
                            passport?.visibility_settings
                              ?.technical_specs_access || 'public',
                          )}
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="small text-muted">Description</div>
                        <div>
                          {product.description ||
                            'No product description provided.'}
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="materials">
                  <Accordion.Header>BOM &amp; SVHC</Accordion.Header>
                  <Accordion.Body>
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openSectionDrawer('bom-svhc')}
                      >
                        <i className="bi bi-pencil-square me-1" />
                        Edit Section
                      </Button>
                    </div>
                    {materials.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Material</th>
                              <th>Weight %</th>
                              <th>Recycled Content</th>
                              <th>Recyclable</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.map((material) => (
                              <tr key={material.id}>
                                <td>{material.material_name}</td>
                                <td>
                                  {formatPercent(material.weight_percentage)}
                                </td>
                                <td>
                                  {formatPercent(
                                    material.recycled_content_percentage,
                                  )}
                                </td>
                                <td>{material.is_recyclable ? 'Yes' : 'No'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-muted">
                        No material records are linked yet.
                      </div>
                    )}

                    {declarations.length > 0 && (
                      <div className="mt-4">
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Compliance declarations
                        </div>
                        <ListGroup>
                          {declarations.map((declaration) => (
                            <ListGroup.Item key={declaration.id}>
                              <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
                                <span className="fw-semibold">
                                  {titleCase(declaration.declaration_type)}
                                </span>
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="border"
                                >
                                  {titleCase(declaration.status)}
                                </Badge>
                              </div>
                              <div className="small text-muted">
                                {declaration.substance_name ||
                                  declaration.present_in_component ||
                                  declaration.scip_number ||
                                  'No additional declaration notes.'}
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="packaging">
                  <Accordion.Header>Packaging</Accordion.Header>
                  <Accordion.Body>
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openSectionDrawer('packaging')}
                      >
                        <i className="bi bi-pencil-square me-1" />
                        Edit Section
                      </Button>
                    </div>
                    {packaging.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Category</th>
                              <th>Material</th>
                              <th>Weight (grams)</th>
                              <th>PCR Content</th>
                              <th>Recyclability</th>
                            </tr>
                          </thead>
                          <tbody>
                            {packaging.map((item) => (
                              <tr key={item.id}>
                                <td>
                                  {item.packaging_category === 'primary'
                                    ? 'Primary / Sales Packaging'
                                    : item.packaging_category === 'secondary'
                                      ? 'Secondary / Grouped Packaging'
                                      : item.packaging_category ===
                                            'tertiary' ||
                                          item.packaging_category ===
                                            'ecommerce'
                                        ? 'Tertiary / Transport Packaging'
                                        : titleCase(
                                            item.packaging_category,
                                          )}
                                </td>
                                <td>{titleCase(item.material_type)}</td>
                                <td>{formatNumber(item.weight_grams, ' g')}</td>
                                <td>
                                  {formatPercent(item.pcr_content_percent)}
                                </td>
                                <td>
                                  {item.recyclability_status
                                    ? titleCase(item.recyclability_status)
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-muted">
                        {productMetadata?.include_consumer_packaging === false
                          ? 'Item comes with no packaging.'
                          : 'No packaging profile has been added yet.'}
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="eco-specs">
                  <Accordion.Header>Eco-Specs</Accordion.Header>
                  <Accordion.Body>
                    <div className="d-flex justify-content-end mb-3">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openSectionDrawer('eco-specs')}
                      >
                        <i className="bi bi-pencil-square me-1" />
                        Edit Section
                      </Button>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="small text-muted">
                          Recycling instructions
                        </div>
                        <div>{circularity?.recycling_instructions || '-'}</div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">Repair manual</div>
                        <div className="text-break">
                          {circularity?.repair_manual_url || '-'}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">
                          Water consumption
                        </div>
                        <div>
                          {formatNumber(
                            circularity?.water_consumption_liters,
                            ' L',
                          )}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">Color fastness</div>
                        <div>
                          {formatNumber(circularity?.color_fastness_rating)}
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="small text-muted">
                          Pilling resistance
                        </div>
                        <div>
                          {formatNumber(circularity?.pilling_resistance_rating)}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">
                          Microplastics status
                        </div>
                        <div>
                          {circularity?.microplastics_status
                            ? titleCase(circularity.microplastics_status)
                            : '-'}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="small text-muted">Care symbols</div>
                        <div>
                          {circularity?.care_symbols?.length
                            ? circularity.care_symbols.join(', ')
                            : '-'}
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Card.Body>
          </Card>
        </Tab> */}

        <Tab eventKey="documents" title="Documents">
          <Card className="product-details-tab-card shadow-sm mt-3">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <div>
                  <div className="text-uppercase small fw-semibold text-muted">
                    Product Attachments
                  </div>
                  <div className="text-muted small">
                    Add compliance documents here. Files linked from the wizard
                    and circularity guides are shown here. You can choose to
                    keep them private or make them public for external access.
                  </div>
                  <div className="text-muted small mt-1">
                    {`${managedDocuments.length}/${MANAGED_DOCUMENT_LIMIT} manual documents used`}
                  </div>
                </div>
                <Button
                  variant="dark"
                  onClick={openAddDocumentModal}
                  disabled={hasReachedManagedDocumentLimit}
                >
                  <i className="bi bi-plus-lg me-2" />
                  Add Compliance Document
                </Button>
              </div>

              {documentActionSuccess && (
                <Alert variant="success">{documentActionSuccess}</Alert>
              )}

              {attachedDocuments.length > 0 ? (
                <ListGroup variant="flush">
                  {attachedDocuments.map((document) => (
                    <ListGroup.Item key={document.id} className="px-0 py-3">
                      <div className="d-flex flex-wrap justify-content-between gap-3">
                        <div>
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <div className="fw-semibold">{document.title}</div>
                            <Badge bg="light" text="dark" className="border">
                              {document.source}
                            </Badge>
                            {document.category && (
                              <Badge bg="secondary">{document.category}</Badge>
                            )}
                            {document.accessLevel && (
                              <Badge
                                bg={
                                  document.accessLevel === 'public'
                                    ? 'success'
                                    : 'warning'
                                }
                                text={
                                  document.accessLevel === 'public'
                                    ? undefined
                                    : 'dark'
                                }
                              >
                                {document.accessLevel === 'public'
                                  ? 'Public'
                                  : 'Restricted'}
                              </Badge>
                            )}
                          </div>
                          {document.uploaded_at && (
                            <div className="small text-muted">
                              Uploaded {formatDateTime(document.uploaded_at)}
                            </div>
                          )}
                          {document.expirationDate && (
                            <div className="small text-muted">
                              Expires {formatDate(document.expirationDate)}
                            </div>
                          )}
                          {document.description && (
                            <div className="small mt-1">
                              {document.description}
                            </div>
                          )}
                          {document.fileSource === 'external_url' &&
                            document.externalUrl && (
                              <div className="small mt-1 text-muted text-break">
                                {document.externalUrl}
                              </div>
                            )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <Button
                            as="a"
                            href={documentUrls[document.id] ?? undefined}
                            target="_blank"
                            rel="noreferrer"
                            variant="outline-dark"
                            disabled={!documentUrls[document.id]}
                          >
                            <i className="bi bi-box-arrow-up-right me-1" />
                            Open File
                          </Button>
                          {document.canManage && (
                            <>
                              <Button
                                variant="outline-secondary"
                                onClick={() => openEditDocumentModal(document)}
                                disabled={documentSaving || documentUploading}
                              >
                                <i className="bi bi-pencil-square me-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                onClick={() =>
                                  void handleDeleteComplianceDocument(document)
                                }
                                disabled={
                                  documentSaving ||
                                  documentUploading ||
                                  documentDeleting
                                }
                              >
                                <i className="bi bi-trash me-1" />
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-muted">
                  No certificates, reports, guides, or linked evidence documents
                  are attached yet.
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab
          eventKey="access-management"
          title={
            <span className="d-inline-flex align-items-center gap-2">
              <span>Access Management</span>
              {hasPendingAccessRequests && (
                <span
                  className="rounded-circle bg-danger"
                  style={{
                    width: '0.6rem',
                    height: '0.6rem',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                  aria-label="Access requests exist"
                  title="Access requests exist"
                />
              )}
            </span>
          }
        >
          <Card className="product-details-tab-card shadow-sm mt-3">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <div className="text-uppercase small fw-semibold text-muted">
                    External Access Control
                  </div>
                  <div className="text-muted small mt-1">
                    Review pending requests and generate tokenized links for
                    restricted or confidential DPP data.
                  </div>
                </div>
                <Badge bg="light" text="dark" className="border">
                  {pendingAccessRequestsCount} pending
                </Badge>
              </div>

              {accessError && <Alert variant="danger">{accessError}</Alert>}
              {accessMessage && (
                <Alert variant="success">{accessMessage}</Alert>
              )}

              {!passport?.id || productStatus === 'DRAFT' ? (
                <Alert variant="warning" className="mb-0">
                  Publish the passport before managing external restricted or
                  confidential access.
                </Alert>
              ) : accessLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Row className="g-4">
                  <Col xl={7}>
                    <Card className="product-details-mini-card shadow-sm h-100">
                      <Card.Body>
                        <div className="text-uppercase small fw-semibold text-muted mb-3">
                          Pending Access Requests
                        </div>
                        {accessRequests.filter(
                          (item) => item.status === 'PENDING',
                        ).length > 0 ? (
                          <ListGroup variant="flush">
                            {accessRequests
                              .filter((item) => item.status === 'PENDING')
                              .map((request) => (
                                <ListGroup.Item
                                  key={request.id}
                                  className="px-0 py-3"
                                >
                                  <div className="d-flex flex-wrap justify-content-between gap-3">
                                    <div>
                                      <div className="fw-semibold">
                                        {request.full_name}
                                      </div>
                                      <div className="small text-muted">
                                        {request.organization} · {request.email}
                                      </div>
                                      <div className="small text-muted mt-1">
                                        {titleCase(request.role)} · Requested{' '}
                                        {formatDateTime(request.requested_at)}
                                      </div>
                                      <div className="small mt-2">
                                        {request.reason}
                                      </div>
                                      <div className="small text-muted mt-2">
                                        Quick grant buttons create a 7-day
                                        access link.
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 align-items-start">
                                      <Button
                                        variant="outline-warning"
                                        disabled={accessActionLoading}
                                        onClick={() =>
                                          void handleAccessDecision('grant', {
                                            requestId: request.id,
                                            tier: 'RESTRICTED',
                                          })
                                        }
                                      >
                                        Grant Restricted
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        disabled={accessActionLoading}
                                        onClick={() =>
                                          void handleAccessDecision('grant', {
                                            requestId: request.id,
                                            tier: 'CONFIDENTIAL',
                                          })
                                        }
                                      >
                                        Grant Confidential
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        disabled={accessActionLoading}
                                        onClick={() =>
                                          void handleAccessDecision('reject', {
                                            requestId: request.id,
                                          })
                                        }
                                      >
                                        Reject
                                      </Button>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                              ))}
                          </ListGroup>
                        ) : (
                          <div className="text-muted mb-0">
                            No pending access requests.
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xl={5}>
                    <Card className="product-details-mini-card shadow-sm mb-4">
                      <Card.Body>
                        <div className="text-uppercase small fw-semibold text-muted mb-3">
                          Generate Direct Link
                        </div>
                        <Form.Group className="mb-3">
                          <Form.Label>Tier</Form.Label>
                          <Form.Select
                            value={directLinkTier}
                            onChange={(event) =>
                              setDirectLinkTier(
                                event.target.value as DppAccessTier,
                              )
                            }
                          >
                            <option value="RESTRICTED">Restricted</option>
                            <option value="CONFIDENTIAL">Confidential</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Expiration</Form.Label>
                          <Form.Select
                            value={directLinkExpiry}
                            onChange={(event) =>
                              setDirectLinkExpiry(
                                event.target.value as
                                  | '24_HOURS'
                                  | '7_DAYS'
                                  | '30_DAYS'
                                  | 'NO_EXPIRY',
                              )
                            }
                          >
                            <option value="24_HOURS">24 Hours</option>
                            <option value="7_DAYS">7 Days</option>
                            <option value="30_DAYS">30 Days</option>
                            <option value="NO_EXPIRY">No Expiry</option>
                          </Form.Select>
                        </Form.Group>
                        <Button
                          variant="dark"
                          disabled={accessActionLoading || !passport.public_url}
                          onClick={() =>
                            void handleAccessDecision('generate_link')
                          }
                        >
                          Generate Direct Link
                        </Button>

                        {generatedDirectLink && (
                          <>
                            <Form.Group className="mt-3">
                              <Form.Label>Magic URL</Form.Label>
                              <Form.Control
                                readOnly
                                value={generatedDirectLink}
                              />
                            </Form.Group>
                            <Button
                              variant="outline-secondary"
                              className="mt-2"
                              onClick={() => void copyGeneratedDirectLink()}
                            >
                              Copy Link
                            </Button>
                          </>
                        )}
                      </Card.Body>
                    </Card>

                    <Card className="product-details-mini-card shadow-sm">
                      <Card.Body>
                        <div className="text-uppercase small fw-semibold text-muted mb-3">
                          Active Tokens
                        </div>
                        {accessTokens.filter((item) => !item.revoked_at)
                          .length > 0 ? (
                          <ListGroup variant="flush">
                            {accessTokens
                              .filter((item) => !item.revoked_at)
                              .slice(0, 6)
                              .map((token) => (
                                <ListGroup.Item
                                  key={token.id}
                                  className="px-0 py-3"
                                >
                                  <div className="d-flex justify-content-between gap-3">
                                    <div>
                                      <div className="fw-semibold">
                                        {token.email}
                                      </div>
                                      <div className="small text-muted">
                                        {token.organization || '-'} ·{' '}
                                        {titleCase(token.access_tier)}
                                      </div>
                                    </div>
                                    <div className="small text-muted text-end">
                                      <div>
                                        Expires{' '}
                                        {token.expires_at
                                          ? formatDateTime(token.expires_at)
                                          : 'Never'}
                                      </div>
                                      <div>
                                        Last used{' '}
                                        {token.last_used_at
                                          ? formatDateTime(token.last_used_at)
                                          : 'Never'}
                                      </div>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                              ))}
                          </ListGroup>
                        ) : (
                          <div className="text-muted">
                            No active external access links yet.
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* <Tab eventKey="public-preview" title="Public Preview">
          <Card className="product-details-public-preview shadow-sm mt-3 border-0">
            <Card.Body className="p-4 p-lg-5">
              <Row className="g-4 align-items-center">
                <Col lg={5}>
                  <div className="text-uppercase small fw-semibold mb-2 opacity-75">
                    Consumer-facing passport
                  </div>
                  <h2 className="h3">{product.name}</h2>
                  <p className="mb-3 opacity-75">
                    {product.description ||
                      'A concise public-facing summary will appear here once the passport is published.'}
                  </p>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Badge bg="light" text="dark">
                      {category?.name || category?.code || 'General Product'}
                    </Badge>
                    <Badge bg="success">{productStatus}</Badge>
                  </div>
                  <div className="small opacity-75">Public URL</div>
                  <div className="text-break">
                    {passport?.public_url || 'Not available yet'}
                  </div>
                </Col>
                <Col lg={7}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div>
                          <div className="small text-muted text-uppercase fw-semibold">
                            Product Snapshot
                          </div>
                          <div className="h5 mb-0 mt-1">Passport Summary</div>
                        </div>
                        <i
                          className="bi bi-phone"
                          style={{ fontSize: '1.5rem' }}
                        />
                      </div>
                      <Row className="g-3">
                        <Col sm={6}>
                          <div className="small text-muted">Brand</div>
                          <div className="fw-semibold">
                            {product.brand_name}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="small text-muted">
                            Expected Lifetime
                          </div>
                          <div className="fw-semibold">
                            {formatNumber(
                              circularity?.expected_lifetime_years,
                              ' years',
                            )}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="small text-muted">Repairability</div>
                          <div className="fw-semibold">
                            {formatNumber(
                              circularity?.repairability_score,
                              ' / 10',
                            )}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="small text-muted">
                            Recyclable Materials
                          </div>
                          <div className="fw-semibold">
                            {
                              materials.filter(
                                (material) => material.is_recyclable,
                              ).length
                            }{' '}
                            / {materials.length || 0}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab> */}

        <Tab eventKey="audit-log" title="Audit Log">
          <Card className="product-details-tab-card shadow-sm mt-3">
            <Card.Body>
              <ListGroup variant="flush">
                {auditEvents.map((event) => (
                  <ListGroup.Item key={event.id} className="px-0 py-3">
                    <div className="d-flex flex-wrap justify-content-between gap-3">
                      <div>
                        <div className="fw-semibold">{event.title}</div>
                        <div className="small text-muted">{event.detail}</div>
                      </div>
                      <div className="small text-muted">
                        {formatDateTime(event.timestamp)}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <Offcanvas
        className="product-details-drawer"
        show={activeSection !== null}
        onHide={closeSectionDrawer}
        placement="end"
      >
        <Offcanvas.Header closeButton={!sectionSaving}>
          <Offcanvas.Title>
            {activeSection === 'basic-info' && 'Edit Basic Info'}
            {activeSection === 'bom-svhc' && 'Edit BOM & SVHC'}
            {activeSection === 'packaging' && 'Edit Packaging'}
            {activeSection === 'eco-specs' && 'Edit Eco-Specs'}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {sectionError && <Alert variant="danger">{sectionError}</Alert>}

          {activeSection === 'basic-info' && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Product Name</Form.Label>
                  <Form.Control
                    value={basicInfoForm.name}
                    onChange={(event) =>
                      setBasicInfoForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>SKU / Model</Form.Label>
                  <Form.Control
                    value={basicInfoForm.skuModel}
                    onChange={(event) =>
                      setBasicInfoForm((current) => ({
                        ...current,
                        skuModel: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Brand Name</Form.Label>
                  <Form.Control
                    value={basicInfoForm.brandName}
                    onChange={(event) =>
                      setBasicInfoForm((current) => ({
                        ...current,
                        brandName: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>GTIN / EAN</Form.Label>
                  <Form.Control
                    value={basicInfoForm.gtinEan}
                    onChange={(event) =>
                      setBasicInfoForm((current) => ({
                        ...current,
                        gtinEan: event.target.value.replace(/\D/g, ''),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={basicInfoForm.categoryCode}
                    onChange={(event) =>
                      setBasicInfoForm((current) => ({
                        ...current,
                        categoryCode: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select a category</option>
                    {categoryOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={basicInfoForm.description}
                    onChange={(event) =>
                      setBasicInfoForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {activeSection === 'bom-svhc' && (
            <div className="d-flex flex-column gap-4">
              <div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="fw-semibold">Bill of Materials</div>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() =>
                      setBomSvhcForm((current) => ({
                        ...current,
                        materials: [
                          ...current.materials,
                          {
                            material_name: '',
                            weight_percentage: '',
                            recycled_content_percentage: '0',
                            is_recyclable: false,
                          },
                        ],
                      }))
                    }
                  >
                    Add Material
                  </Button>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>Weight %</th>
                        <th>Recycled %</th>
                        <th>Recyclable</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {bomSvhcForm.materials.map((material, index) => (
                        <tr key={`${material.material_name}-${index}`}>
                          <td>
                            <Form.Control
                              size="sm"
                              value={material.material_name}
                              onChange={(event) =>
                                setBomSvhcForm((current) => ({
                                  ...current,
                                  materials: current.materials.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            material_name: event.target.value,
                                          }
                                        : item,
                                  ),
                                }))
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              value={material.weight_percentage}
                              onChange={(event) =>
                                setBomSvhcForm((current) => ({
                                  ...current,
                                  materials: current.materials.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            weight_percentage:
                                              event.target.value,
                                          }
                                        : item,
                                  ),
                                }))
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              value={material.recycled_content_percentage}
                              onChange={(event) =>
                                setBomSvhcForm((current) => ({
                                  ...current,
                                  materials: current.materials.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            recycled_content_percentage:
                                              event.target.value,
                                          }
                                        : item,
                                  ),
                                }))
                              }
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              checked={material.is_recyclable}
                              onChange={(event) =>
                                setBomSvhcForm((current) => ({
                                  ...current,
                                  materials: current.materials.map(
                                    (item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            is_recyclable: event.target.checked,
                                          }
                                        : item,
                                  ),
                                }))
                              }
                            />
                          </td>
                          <td>
                            <Button
                              variant="link"
                              className="p-0 text-danger"
                              onClick={() =>
                                setBomSvhcForm((current) => ({
                                  ...current,
                                  materials:
                                    current.materials.length > 1
                                      ? current.materials.filter(
                                          (_, itemIndex) => itemIndex !== index,
                                        )
                                      : current.materials,
                                }))
                              }
                            >
                              <i className="bi bi-trash" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <Form.Label>REACH SVHC Status</Form.Label>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <Form.Check
                    inline
                    type="radio"
                    label="No SVHCs present"
                    name="detailSvhcStatus"
                    checked={bomSvhcForm.svhcStatus === 'none'}
                    onChange={() =>
                      setBomSvhcForm((current) => ({
                        ...current,
                        svhcStatus: 'none',
                        svhcDeclarations: [],
                      }))
                    }
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Contains SVHC > 0.1% w/w"
                    name="detailSvhcStatus"
                    checked={bomSvhcForm.svhcStatus === 'contains'}
                    onChange={() =>
                      setBomSvhcForm((current) => ({
                        ...current,
                        svhcStatus: 'contains',
                        svhcDeclarations:
                          current.svhcDeclarations.length > 0
                            ? current.svhcDeclarations
                            : [
                                {
                                  substance_name: '',
                                  cas_ec_number: '',
                                  present_in_component: '',
                                  scip_number: '',
                                  document_id: null,
                                },
                              ],
                      }))
                    }
                  />
                </div>

                {bomSvhcForm.svhcStatus === 'contains' && (
                  <>
                    <div className="d-flex justify-content-end mb-2">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() =>
                          setBomSvhcForm((current) => ({
                            ...current,
                            svhcDeclarations: [
                              ...current.svhcDeclarations,
                              {
                                substance_name: '',
                                cas_ec_number: '',
                                present_in_component: '',
                                scip_number: '',
                                document_id: null,
                              },
                            ],
                          }))
                        }
                      >
                        Add Substance
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Substance</th>
                            <th>CAS / EC</th>
                            <th>Component</th>
                            <th>SCIP</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {bomSvhcForm.svhcDeclarations.map(
                            (declaration, index) => (
                              <tr
                                key={`${declaration.substance_name}-${index}`}
                              >
                                <td>
                                  <Form.Control
                                    size="sm"
                                    value={declaration.substance_name}
                                    onChange={(event) =>
                                      setBomSvhcForm((current) => ({
                                        ...current,
                                        svhcDeclarations:
                                          current.svhcDeclarations.map(
                                            (item, itemIndex) =>
                                              itemIndex === index
                                                ? {
                                                    ...item,
                                                    substance_name:
                                                      event.target.value,
                                                  }
                                                : item,
                                          ),
                                      }))
                                    }
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    value={declaration.cas_ec_number}
                                    onChange={(event) =>
                                      setBomSvhcForm((current) => ({
                                        ...current,
                                        svhcDeclarations:
                                          current.svhcDeclarations.map(
                                            (item, itemIndex) =>
                                              itemIndex === index
                                                ? {
                                                    ...item,
                                                    cas_ec_number:
                                                      event.target.value,
                                                  }
                                                : item,
                                          ),
                                      }))
                                    }
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    value={declaration.present_in_component}
                                    onChange={(event) =>
                                      setBomSvhcForm((current) => ({
                                        ...current,
                                        svhcDeclarations:
                                          current.svhcDeclarations.map(
                                            (item, itemIndex) =>
                                              itemIndex === index
                                                ? {
                                                    ...item,
                                                    present_in_component:
                                                      event.target.value,
                                                  }
                                                : item,
                                          ),
                                      }))
                                    }
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    value={declaration.scip_number}
                                    onChange={(event) =>
                                      setBomSvhcForm((current) => ({
                                        ...current,
                                        svhcDeclarations:
                                          current.svhcDeclarations.map(
                                            (item, itemIndex) =>
                                              itemIndex === index
                                                ? {
                                                    ...item,
                                                    scip_number:
                                                      event.target.value,
                                                  }
                                                : item,
                                          ),
                                      }))
                                    }
                                  />
                                </td>
                                <td>
                                  <Button
                                    variant="link"
                                    className="p-0 text-danger"
                                    onClick={() =>
                                      setBomSvhcForm((current) => ({
                                        ...current,
                                        svhcDeclarations:
                                          current.svhcDeclarations.filter(
                                            (_, itemIndex) =>
                                              itemIndex !== index,
                                          ),
                                      }))
                                    }
                                  >
                                    <i className="bi bi-trash" />
                                  </Button>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeSection === 'packaging' && (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="fw-semibold">Packaging Items</div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() =>
                    setPackagingForm((current) => ({
                      ...current,
                      packagingItems: [
                        ...current.packagingItems,
                        {
                          packaging_category: '',
                          material_type: '',
                          weight_grams: '',
                          pcr_content_percent: '',
                          recyclability_status: '',
                        },
                      ],
                    }))
                  }
                >
                  Add Packaging
                </Button>
              </div>

              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Material</th>
                      <th>Weight (grams)</th>
                      <th>PCR %</th>
                      <th>Recyclability</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {packagingForm.packagingItems.map((item, index) => (
                      <tr key={`${item.packaging_category}-${index}`}>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.packaging_category}
                            onChange={(event) =>
                              setPackagingForm((current) => ({
                                ...current,
                                packagingItems: current.packagingItems.map(
                                  (row, rowIndex) =>
                                    rowIndex === index
                                      ? {
                                          ...row,
                                          packaging_category:
                                            event.target.value,
                                        }
                                      : row,
                                ),
                              }))
                            }
                          >
                            <option value="">Select</option>
                            <option value="primary">
                              Primary / Sales Packaging
                            </option>
                            <option value="secondary">
                              Secondary / Grouped Packaging
                            </option>
                            <option value="tertiary">
                              Tertiary / Transport Packaging
                            </option>
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.material_type}
                            onChange={(event) =>
                              setPackagingForm((current) => ({
                                ...current,
                                packagingItems: current.packagingItems.map(
                                  (row, rowIndex) =>
                                    rowIndex === index
                                      ? {
                                          ...row,
                                          material_type: event.target.value,
                                        }
                                      : row,
                                ),
                              }))
                            }
                          >
                            <option value="">Select</option>
                            <option value="pap20">PAP 20</option>
                            <option value="pap21">PAP 21</option>
                            <option value="ldpe04">LDPE 04</option>
                            <option value="pet01">PET 01</option>
                            <option value="wood">Wood</option>
                            <option value="other">Other</option>
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={item.weight_grams}
                            onChange={(event) =>
                              setPackagingForm((current) => ({
                                ...current,
                                packagingItems: current.packagingItems.map(
                                  (row, rowIndex) =>
                                    rowIndex === index
                                      ? {
                                          ...row,
                                          weight_grams: event.target.value,
                                        }
                                      : row,
                                ),
                              }))
                            }
                          />
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            value={item.pcr_content_percent}
                            onChange={(event) =>
                              setPackagingForm((current) => ({
                                ...current,
                                packagingItems: current.packagingItems.map(
                                  (row, rowIndex) =>
                                    rowIndex === index
                                      ? {
                                          ...row,
                                          pcr_content_percent:
                                            event.target.value,
                                        }
                                      : row,
                                ),
                              }))
                            }
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.recyclability_status}
                            onChange={(event) =>
                              setPackagingForm((current) => ({
                                ...current,
                                packagingItems: current.packagingItems.map(
                                  (row, rowIndex) =>
                                    rowIndex === index
                                      ? {
                                          ...row,
                                          recyclability_status:
                                            event.target.value,
                                        }
                                      : row,
                                ),
                              }))
                            }
                          >
                            <option value="">Select</option>
                            <option value="recyclable_100">
                              100% Recyclable
                            </option>
                            <option value="requires_separation">
                              Requires Separation
                            </option>
                            <option value="non_recyclable">
                              Non-Recyclable
                            </option>
                          </Form.Select>
                        </td>
                        <td>
                          <Button
                            variant="link"
                            className="p-0 text-danger"
                            onClick={() =>
                              setPackagingForm((current) => ({
                                ...current,
                                packagingItems: current.packagingItems.filter(
                                  (_, rowIndex) => rowIndex !== index,
                                ),
                              }))
                            }
                          >
                            <i className="bi bi-trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Form.Group>
                <Form.Label>Packaging EPR Registration</Form.Label>
                <Form.Control
                  value={packagingForm.packagingEprRegistration}
                  onChange={(event) =>
                    setPackagingForm((current) => ({
                      ...current,
                      packagingEprRegistration: event.target.value,
                    }))
                  }
                />
              </Form.Group>
            </div>
          )}

          {activeSection === 'eco-specs' && (
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Carbon Footprint</Form.Label>
                  <Form.Control
                    type="number"
                    value={ecoSpecsForm.carbonFootprint}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        carbonFootprint: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Expected Lifetime (Years)</Form.Label>
                  <Form.Control
                    type="number"
                    value={ecoSpecsForm.expectedLifetimeYears}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        expectedLifetimeYears: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Repairability Score</Form.Label>
                  <Form.Control
                    type="number"
                    value={ecoSpecsForm.repairabilityScore}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        repairabilityScore: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Repair Guide URL</Form.Label>
                  <Form.Control
                    value={ecoSpecsForm.repairGuideUrl}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        repairGuideUrl: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Recycling Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={ecoSpecsForm.recyclingInstructions}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        recyclingInstructions: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Water Consumption</Form.Label>
                  <Form.Control
                    type="number"
                    value={ecoSpecsForm.waterConsumption}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        waterConsumption: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Microplastics Status</Form.Label>
                  <Form.Select
                    value={ecoSpecsForm.microplasticsStatus}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        microplasticsStatus: event.target
                          .value as EcoSpecsFormState['microplasticsStatus'],
                      }))
                    }
                  >
                    <option value="">Select</option>
                    <option value="not_applicable">Not Applicable</option>
                    <option value="untested">Untested</option>
                    <option value="tested">Tested</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Color Fastness</Form.Label>
                  <Form.Control
                    type="number"
                    value={ecoSpecsForm.colorFastnessRating}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        colorFastnessRating: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Pilling Resistance</Form.Label>
                  <Form.Control
                    type="number"
                    value={ecoSpecsForm.pillingResistanceRating}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        pillingResistanceRating: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Spare Parts Availability</Form.Label>
                  <Form.Control
                    value={ecoSpecsForm.sparePartsAvailability}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        sparePartsAvailability: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Take-Back Scheme</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={ecoSpecsForm.takeBackScheme}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        takeBackScheme: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>EPR Registration</Form.Label>
                  <Form.Control
                    value={ecoSpecsForm.eprRegistrationNumber}
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        eprRegistrationNumber: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Care Symbols</Form.Label>
                  <Form.Control
                    value={ecoSpecsForm.careSymbols}
                    placeholder="Separate values with commas"
                    onChange={(event) =>
                      setEcoSpecsForm((current) => ({
                        ...current,
                        careSymbols: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <Button
              variant="outline-secondary"
              onClick={closeSectionDrawer}
              disabled={sectionSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveSection}
              disabled={sectionSaving}
            >
              {sectionSaving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Section'
              )}
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Modal show={showLifecycleModal} onHide={closeLifecycleModal} centered>
        <Modal.Header closeButton={!lifecycleSaving}>
          <Modal.Title>Update Product Lifecycle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Hard delete is blocked because this product already has a public
            passport history. Update the passport lifecycle instead to preserve
            traceability.
          </Alert>

          <p className="mb-3">
            <strong>{product?.name}</strong> is currently{' '}
            <strong>Published</strong>.
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Lifecycle Action</Form.Label>
            <div className="d-flex flex-column gap-2">
              <Form.Check
                type="radio"
                name="productLifecycleAction"
                id="productLifecycleDiscontinue"
                checked={lifecycleAction === 'DISCONTINUE'}
                onChange={() => setLifecycleAction('DISCONTINUE')}
                label="Discontinue product and archive published passports"
              />
              <Form.Check
                type="radio"
                name="productLifecycleAction"
                id="productLifecycleWithdraw"
                checked={lifecycleAction === 'WITHDRAW'}
                onChange={() => setLifecycleAction('WITHDRAW')}
                label="Withdraw product and replace public specifications with a revocation notice"
              />
            </div>
          </Form.Group>

          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={lifecycleReason}
              onChange={(event) => setLifecycleReason(event.target.value)}
              placeholder={
                lifecycleAction === 'WITHDRAW'
                  ? 'Explain why this product is being withdrawn.'
                  : 'Explain why this product is being discontinued.'
              }
            />
          </Form.Group>

          {lifecycleError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {lifecycleError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeLifecycleModal}
            disabled={lifecycleSaving}
          >
            Cancel
          </Button>
          <Button
            variant={lifecycleAction === 'WITHDRAW' ? 'danger' : 'warning'}
            onClick={() => void handleLifecycleUpdate()}
            disabled={lifecycleSaving}
          >
            {lifecycleSaving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Working...
              </>
            ) : lifecycleAction === 'WITHDRAW' ? (
              'Withdraw Product'
            ) : (
              'Discontinue Product'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPreviewModal}
        onHide={closePreviewModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview Passport</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="p-0 bg-light"
          style={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          {previewPayload ? (
            <PublicProductDppView
              payload={previewPayload}
              previewMode
              showHomeLink={false}
            />
          ) : (
            <div className="p-4 text-muted">Preview is unavailable.</div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPublishConfirmModal}
        onHide={closePublishConfirmModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton={!publishing}>
          <Modal.Title>🚀 Confirm Passport Publication</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-4">
            Publishing generates a live, public Digital Product Passport URL
            compliant with EU ESPR regulations.
          </Alert>

          <Form.Check
            id="publishConfirmDataAccuracy"
            className="mb-3"
            checked={publishConfirmDataAccuracy}
            onChange={(event) =>
              setPublishConfirmDataAccuracy(event.target.checked)
            }
            label="I confirm that all chemical, material, packaging, and supply chain origin data provided are accurate and backed by valid supporting documentation in our records."
          />

          <Form.Check
            id="publishConfirmPublicUrl"
            checked={publishConfirmPublicUrl}
            onChange={(event) =>
              setPublishConfirmPublicUrl(event.target.checked)
            }
            label="I understand that under EU ESPR rules, once published, this passport cannot be hard-deleted and its public URL will remain permanently accessible."
          />
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={closePublishConfirmModal}
            disabled={publishing}
          >
            Cancel
          </Button>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={() => void handlePublishPassport()}
              disabled={!canConfirmPublish || publishing}
            >
              {publishing ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Publishing...
                </>
              ) : (
                'Confirm & Publish'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showDocumentModal} onHide={closeDocumentModal} centered>
        <Modal.Header closeButton={!documentSaving && !documentUploading}>
          <Modal.Title>
            {editingDocument ? 'Edit Document' : 'Add Document'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(documentActionError || documentUploadError) && (
            <Alert variant="danger" className="mb-3">
              {documentActionError || documentUploadError}
            </Alert>
          )}
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Document Category</Form.Label>
                <Form.Select
                  value={documentForm.category}
                  onChange={(event) =>
                    setDocumentFormValue(
                      'category',
                      event.target.value as ComplianceDocumentCategory,
                    )
                  }
                >
                  {complianceDocumentCategoryOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Document Title *</Form.Label>
                <Form.Control
                  required
                  value={documentForm.title}
                  isInvalid={
                    documentActionError === 'Document title is required.'
                  }
                  onChange={(event) =>
                    setDocumentFormValue('title', event.target.value)
                  }
                  placeholder="Required"
                />
                <Form.Control.Feedback type="invalid">
                  Document title is required.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* <Col xs={12}>
              <Form.Group>
                <Form.Label>File Source</Form.Label>
                <div className="d-flex flex-wrap gap-3 mt-1">
                  <Form.Check
                    type="radio"
                    id="document-source-file"
                    name="documentSource"
                    label="File Drag-and-Drop"
                    checked={documentForm.fileSource === 'file'}
                    onChange={() =>
                      setDocumentForm((current) => ({
                        ...current,
                        fileSource: 'file',
                        externalUrl: '',
                      }))
                    }
                  />
                  <Form.Check
                    type="radio"
                    id="document-source-url"
                    name="documentSource"
                    label="External URL"
                    checked={documentForm.fileSource === 'external_url'}
                    onChange={() =>
                      setDocumentForm((current) => ({
                        ...current,
                        fileSource: 'external_url',
                        file: null,
                      }))
                    }
                  />
                </div>
              </Form.Group>
            </Col> */}
            <Col xs={12}>
              {documentForm.fileSource === 'file' ? (
                <Form.Group>
                  <Form.Label>Upload File</Form.Label>
                  <div
                    className={`border rounded p-3 text-center ${documentDropActive ? 'bg-light border-dark' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => complianceDocumentInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        complianceDocumentInputRef.current?.click();
                      }
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDocumentDropActive(true);
                    }}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      setDocumentDropActive(false);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDocumentDropActive(false);
                      handleComplianceFileSelected(
                        event.dataTransfer.files?.[0] ?? null,
                      );
                    }}
                  >
                    <div className="fw-semibold mb-1">
                      Drop PDF, PNG, or JPG here
                    </div>
                    <div className="small text-muted">
                      or click to choose a file up to 25MB.
                    </div>
                    {documentForm.file && (
                      <div className="small mt-2 text-muted">
                        Selected: {documentForm.file.name}
                      </div>
                    )}
                    {!documentForm.file &&
                      editingDocument?.fileSource === 'file' && (
                        <div className="small mt-2 text-muted">
                          Leave empty to keep the current uploaded file.
                        </div>
                      )}
                  </div>
                  <Form.Control
                    ref={complianceDocumentInputRef}
                    className="d-none"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    onChange={(event) => {
                      const input = event.target as unknown as HTMLInputElement;
                      handleComplianceFileSelected(input.files?.[0] ?? null);
                    }}
                  />
                </Form.Group>
              ) : (
                <Form.Group>
                  <Form.Label>External URL</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://example.com/document.pdf"
                    value={documentForm.externalUrl}
                    onChange={(event) =>
                      setDocumentFormValue('externalUrl', event.target.value)
                    }
                  />
                </Form.Group>
              )}
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Access Level</Form.Label>
                <div className="d-flex flex-column gap-2 mt-1">
                  <Form.Check
                    type="radio"
                    id="document-access-public"
                    name="documentAccessLevel"
                    label="Public (Show on Consumer QR Page)"
                    checked={documentForm.accessLevel === 'public'}
                    onChange={() =>
                      setDocumentFormValue('accessLevel', 'public')
                    }
                  />
                  <Form.Check
                    type="radio"
                    id="document-access-restricted"
                    name="documentAccessLevel"
                    label="Restricted (Internal & Market Surveillance Only)"
                    checked={documentForm.accessLevel === 'restricted'}
                    onChange={() =>
                      setDocumentFormValue('accessLevel', 'restricted')
                    }
                  />
                </div>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Expiration Date</Form.Label>
                <Form.Control
                  type="date"
                  value={documentForm.expirationDate}
                  onChange={(event) =>
                    setDocumentFormValue('expirationDate', event.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeDocumentModal}
            disabled={documentSaving || documentUploading}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={() => void handleSaveComplianceDocument()}
            disabled={documentSaving || documentUploading}
          >
            {documentSaving || documentUploading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </>
            ) : editingDocument ? (
              'Save Changes'
            ) : (
              'Add Document'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
